/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { Location } from "../../models/location";
import { LocationInstance } from "../../models/location-instance";
import { Zone } from "../../models/zone";
import { ZoneInstance } from "../../models/zone-instance";
import { LocationService } from "../../services/location.service";
import { Component, OnInit, OnDestroy, ChangeDetectorRef, ViewChild, ElementRef, AfterViewInit } from "@angular/core";
import * as createjs from 'createjs-module';

import { FEET_TO_METERS_MULTIPLIER } from "../../app-constants";
import { AntennaInstance } from "../../models/antenna-instance";
import { Color } from "../../models/color";
import { Image } from "../../models/image";
import { ObjectInstance } from "../../models/object-instance";
import { Point } from "../../models/point";
import { ReaderInstance } from "../../models/reader-instance";
import { AntennaService } from "../../services/antenna.service";
import { CanComponentDeactivate } from "../../services/can-deactivate-guard.service";
import { ImageService } from "../../services/image.service";
import { ObjectService } from "../../services/object.service";
import { ReaderService } from "../../services/reader.service";
import { ZoneService } from "../../services/zone.service";
import { CreateSiteDialogComponent } from "./create-site-dialog.component";
import { LocationChangesDialogComponent, LocationChangeUserResponse } from "./location-changes-dialog.component";
import { SetScaleFinishDialogComponent } from "./set-scale-finish-dialog.component";
import { SetScaleStartDialogComponent } from "./set-scale-start-dialog.component";
import { UploadingDialogComponent } from "./uploading-dialog.component";
import { ParentLocationWrapper } from "../../models/jaxb/parent-location-wrapper";
import {SetZCoordinateDialogComponent} from "./set-z-coordinate-dialog.component";
import { DisplayModule } from "../../shared/display.module";
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from "../simple-success-error-dialog-box/simple-success-error-dialog-box.component";




declare var $: any;

const DEFAULT_UNKNOWN = "N/A";

const MINIMAP_CANVAS_ID = "sitesMiniCanvas";
const MINIMAP_DIMENSION_IN_PIXELS = 175;
const LOCATION_TAB_ID = "locationTab";
const SUBLOCATIONS_TAB_ID = "subLocationsTab";
const ZONES_TAB_ID = "zonesTab";
const OBJECTS_TAB_ID = "objectsTab";
const MAIN_CANVAS_ID = "sitesMainCanvas";
const MAIN_CANVAS_WIDTH_IN_PIXELS = 1250;
const MAIN_CANVAS_HEIGHT_IN_PIXELS = 714;

const SCALE_INDEX_MULTIPLIER = .05;
const MIN_SCALE_INDEX = 1;
const SCALE_INDEX_100_PERCENT = 20;
const MAX_SCALE_INDEX = 200;

const ZOOM_SLIDER_ID = "zoomSliderDiv";

@Component({
  templateUrl: './sites.component.html',
  styleUrls: ['./sites.component.css']
})
export class SitesComponent implements OnInit, OnDestroy, AfterViewInit, CanComponentDeactivate {


  //UI Views referenced/used by this Component
  @ViewChild('scaleStartDialog')
  mSetScaleStartDialog: SetScaleStartDialogComponent;

  @ViewChild('scaleFinishDialog')
  mSetScaleFinishDialog: SetScaleFinishDialogComponent;

  @ViewChild('createSiteDialog')
  mCreateSiteDialog: CreateSiteDialogComponent;

  @ViewChild('locationImage')
  mLocationImageFileInput: ElementRef;

  @ViewChild('uploadingDialog')
  mUploadingDialog: UploadingDialogComponent;

  @ViewChild('setZCoordinateDialog')
  mSetZCoordinateDialog: SetZCoordinateDialogComponent;

  @ViewChild(LocationChangesDialogComponent)
  mLocationChangesDialog: LocationChangesDialogComponent;

  /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;



  //All known Location Types - Loaded on Component Initialization
  mLocationTypes: Location[] = [];

  //All known Zone Types - Loaded on Component Initialization
  mZoneTypes: Zone[] = [];


  //Holds the selected Main Tab (SITES/LOCATION/ZONES/OBJECTS)
  mSelectedMainTab: MainTabType = MainTabType.SITES;


  //All Site Locations - Loaded on Component Initialization
  mSiteLocations: LocationInstance[] = [];

  //Location that was selected
  mSelectedLocation: LocationInstance = null;

  //Copy of the Location that was selected, information is shown in the UI so that the User can edit it
  mEditableLocation: LocationInstance = null;

  mSubLocationUis: SubLocationUi[] = [];
  mSelectedSubLocationUi: SubLocationUi = null;
  mSelectedSubLocationUiColor: string;
  mSelectedSubLocationUiTextColor: string;

  //Zones that were found inside the current selected Location which should be displayed
  mZoneUis: ZoneUi[] = [];

  //Zone UI that was selected by the User and is being displayed
  mSelectedZoneUi: ZoneUi = null;

  //Selected Zone UI's Color for use with the HTML5 <input type="color">.  This is what is modeled/changed.
  mSelectedZoneUiColor: string;
  mSelectedZoneUiTextColor: string;

  //Holds the selected Object Tab (ANTENNAS/READERS/DEVICES/ASSETS)
  mSelectedObjectTab: ObjectTabType = ObjectTabType.ANTENNAS;


  mAntennas: AntennaInstance[] = [];
  mAntennasInLocation: AntennaUi[] = [];
  mAntennasNotInLocation: AntennaUi[] = [];
  mSelectedAntennaUi: AntennaUi = null;

  mReaders: ReaderInstance[] = [];
  mReadersInLocation: ReaderUi[] = [];
  mReadersNotInLocation: ReaderUi[] = [];
  mSelectedReaderUi: ReaderUi = null;

  mDevices: ObjectInstance[] = [];
  mDevicesInLocation: ObjectUi[] = [];
  mDevicesNotInLocation: ObjectUi[] = [];
  mSelectedDeviceUi: ObjectUi = null;

  mAssets: ObjectInstance[] = [];
  mAssetsInLocation: ObjectUi[] = [];
  mAssetsNotInLocation: ObjectUi[] = [];
  mSelectedAssetUi: ObjectUi = null;

  mCurrentPos: number[] = null;

  mIsPlacing = false;
  mCancelClickEvent = false;

  mIsDrawingSubLocation = false;
  mIsDrawingZone = false;
  mTemporaryPerimeterPoints: PerimeterPointUi[] = null;
  mTemporaryShape: createjs.Shape = null;

  mIsSettingScale = false;
  mScaleStartPoint: PerimeterPointUi = null;
  mScaleStopPoint: PerimeterPointUi = null;




  //UI Displays
  mMiniMapHeightDisplay = DEFAULT_UNKNOWN;
  mMiniMapWidthDisplay = DEFAULT_UNKNOWN;
  mLocationScaleDisplay = DEFAULT_UNKNOWN;
  mMiniMapStage: createjs.Stage = null;
  mMiniMapImage: createjs.Bitmap = null;
  mMainMapStage: createjs.Stage = null;
  mMainMapImage: createjs.Bitmap = null;
  mMainMapScaleIndex = SCALE_INDEX_100_PERCENT;
  mMainCanvasPageX: number;
  mMainCanvasPageY: number;

  //UI Constants (Mapped to a variables here so it can be referenced in the HTML)
  mMainTabTypes = MainTabType;
  mMiniMapCanvasId = MINIMAP_CANVAS_ID;
  mMiniMapSize = MINIMAP_DIMENSION_IN_PIXELS;
  mLocationTabId = LOCATION_TAB_ID;
  mSubLocationsTabId = SUBLOCATIONS_TAB_ID;
  mZonesTabId = ZONES_TAB_ID;
  mObjectsTabId = OBJECTS_TAB_ID;
  mMainCanvasId = MAIN_CANVAS_ID;
  mMainCanvasWidthInPixels = MAIN_CANVAS_WIDTH_IN_PIXELS;
  mMainCanvasHeightInPixels = MAIN_CANVAS_HEIGHT_IN_PIXELS;
  mZoomSliderId = ZOOM_SLIDER_ID;
  mZoomMinValue = MIN_SCALE_INDEX;
  mZoomMaxValue = MAX_SCALE_INDEX;
  mObjectTabTypes = ObjectTabType;

  //createjs.Ticker "tick" event listener, used to update the MiniMap and Main Map Stages.  Function gets attached in ngOnInit() and removed in ngOnDestroy().
  mOnTickFunction: Function;





  constructor(
    private cd: ChangeDetectorRef,
    private mAntennaService: AntennaService,
    private mImageService: ImageService,
    private mLocationService: LocationService,
    private mObjectService: ObjectService,
    private mReaderService: ReaderService,
    private mZoneService: ZoneService
  ) { }





  ngOnInit(): void {
    //Load the Site Locations
    this.refreshSiteLocations();

    //Load the Location Types
    this.mLocationService
      .getAllLocations()
      .then(
        locationTypes => {
          this.mLocationTypes = locationTypes;
        },
        error => this.handleError(error)
      );

    //Load the Zone Types
    this.mZoneService
      .getAllZones()
      .then(
        zoneTypes => {
          this.mZoneTypes = zoneTypes;
        },
        error => this.handleError(error)
      );
  }

  ngAfterViewInit(): void {
    //Get the Stage for the MiniMap Location Image
    this.mMiniMapStage = new createjs.Stage(this.mMiniMapCanvasId);
    this.mMiniMapStage.name = "mini-map";

    //Get the Stage for the Main Location Image
    this.mMainMapStage = new createjs.Stage(this.mMainCanvasId);
    this.mMainMapStage.name = "main-map";//Get the Main Canvas, set up scrolling using the Mouse Wheel and panning using Shift + Mouse down/move/release

    //Attach a function to run on each "tick" of the createjs.Ticker, used to update the Stages to display any changes.
    const component = this;
    this.mOnTickFunction = createjs.Ticker.on("tick", function(event: createjs.TickerEvent): void {
      component.mMiniMapStage.update();
      component.mMainMapStage.update();
    });

    this.mMainMapStage.mouseMoveOutside = true;
    this.mMainMapStage.on("click", function(event: createjs.MouseEvent): void {
      console.log("Stage Mouse Click! Event: " + event + " - Canceled? " + component.mCancelClickEvent + " - Setting Scale? " + component.mIsSettingScale);
      if (!component.mCancelClickEvent) {
        if (!component.mIsDrawingZone && !component.mIsDrawingSubLocation) {
          if (component.mIsSettingScale) {
            if (component.mScaleStartPoint == null) {
              component.mScaleStartPoint = new PerimeterPointUi();
              component.mScaleStartPoint.mPoint = component.getPointFromStageXY(event.stageX, event.stageY);
              component.drawPerimeterPoint(component.mScaleStartPoint);
            } else {
              //Get the start/end points in Pixels
              const endPoint = component.getPointFromStageXY(event.stageX, event.stageY);
              const startX = component.translateMetersToPixels(component.mScaleStartPoint.mPoint.x);
              const startY = component.translateMetersToPixels(component.mScaleStartPoint.mPoint.y);
              const endX = component.translateMetersToPixels(endPoint.x);
              const endY = component.translateMetersToPixels(endPoint.y);

              //Figure out the distance between the start/end points in pixels [d = SQRT((Bx - Ax)^2 - (By - Ay)^2)] d = distance, A and B are the points
              const distance = Math.sqrt(Math.pow((endX - startX), 2) + Math.pow((endY - startY), 2));
              console.log("Scale Distance: " + distance);

              //Show the Finish Dialog where the User enters in the actual units of measurement for the measured number of pixels
              component.mSetScaleFinishDialog.show(distance);

              //Cancel the Scaling to reset everything back to normal
              component.setScaleCanceled();
            }
          } else if (component.mIsPlacing) {
            event.stopPropagation();
          } else {
            if (component.mSelectedAntennaUi != null) {
              component.selectAntennaUi(null);
            }
            if (component.mSelectedReaderUi != null) {
              component.selectReaderUi(null);
            }
            if (component.mSelectedAssetUi != null) {
              component.selectAssetUi(null);
            }
            if (component.mSelectedDeviceUi != null) {
              component.selectDeviceUi(null);
            }
            if (component.mSelectedZoneUi != null) {
              component.selectZoneUi(null);
            }
            if (component.mSelectedSubLocationUi != null) {
              component.selectSubLocationUi(null);
            }
            event.stopPropagation();
          }
        }
      }
    });
  }

  ngOnDestroy(): void {
    //Clean up/Remove the function attached to the "tick" events of the createjs.Ticker
    createjs.Ticker.off("tick", this.mOnTickFunction);
  }





  onMouseWheel(event: WheelEvent): boolean {
    //Scale the Image based on how hard/fast the User scrolled the wheel
    this.setMainMapScaleIndex(this.mMainMapScaleIndex + (event.deltaY / 100));

    //Return false to prevent the page from scrolling
    return false;
  }

  onMouseDown(event: MouseEvent): void {
    if (event.shiftKey) {
      this.mCurrentPos = [event.pageX, event.pageY];
      this.mCancelClickEvent = true;
      return;
    }

    if (this.mIsDrawingZone || this.mIsDrawingSubLocation) {
      //If the point the mouse was pressed down on is already a Temporary Point for the Zone... Ignore it unless it is the first point and there are at least 2 other points, in which case the Zone should be finished
      const otherUi = this.mTemporaryPerimeterPoints.find(ui => ui.hitTest(event.offsetX, event.offsetY));
      if (otherUi != null) {
        //Other Point was pressed!  If it was the first point and there are 2 others, then finish drawing the Zone... Otherwise ignore it
        if (otherUi == this.mTemporaryPerimeterPoints[0]) {
          if (this.mIsDrawingZone) {
            this.finishDrawingZone();
          } else {
            this.finishDrawingSubLocation();
          }
        }
      } else {
        //Mouse was pressed on a point where there isn't already a Temporary Point for the new Zone... Create the new Point where the mouse was pressed
        const ui = new PerimeterPointUi();
        ui.mPoint = this.getPointFromStageXY(event.offsetX, event.offsetY);  //Set the Point to the position the mouse was pressed. (Layer X/Y seems to be a rounded int value of Stage X/Y so it should be good enough)
        this.mTemporaryPerimeterPoints.push(ui);

        //Draw the new Point on the screen
        this.drawPerimeterPoint(ui);
      }
      this.mCancelClickEvent = true;
    }
    this.mCancelClickEvent = false;
  }

  onMouseMove(event: MouseEvent): void {
    //Keep track of the Page X/Y from these events so that the popups will work when icons are clicked.
    this.mMainCanvasPageX = event.pageX;
    this.mMainCanvasPageY = event.pageY;

    if (this.mCurrentPos != null) {
      if (event.shiftKey) {
        this.mMainMapStage.x = this.mMainMapStage.x + (event.pageX - this.mCurrentPos[0]);
        this.mMainMapStage.y = this.mMainMapStage.y + (event.pageY - this.mCurrentPos[1]);
        this.mCurrentPos[0] = event.pageX;
        this.mCurrentPos[1] = event.pageY;
      } else {
        this.mCurrentPos = null;
      }
    } else if (this.mIsSettingScale) {
      //Draw a line between the Scale Start point and the current mouse position
      if (this.mScaleStartPoint != null) {
        if (this.mScaleStopPoint == null) {
          this.mScaleStopPoint = new PerimeterPointUi();
        } else if (this.mScaleStopPoint.mShape != null) {
          this.mMainMapStage.removeChild(this.mScaleStopPoint.mShape);
          this.mScaleStopPoint.mShape = null;
        }

        //Get the information on the point where the mouse was moved on the Canvas
        const point = this.getPointFromStageXY(event.offsetX, event.offsetY);
        const pointX = this.translateMetersToPixels(point.x);
        const pointY = this.translateMetersToPixels(point.y);

        //Get the information from the starting point so we know where to start the line...
        const startX = this.translateMetersToPixels(this.mScaleStartPoint.mPoint.x);
        const startY = this.translateMetersToPixels(this.mScaleStartPoint.mPoint.y);

        //Draw a line connecting the first point and the current mouse position
        this.mScaleStopPoint.mShape = new createjs.Shape();
        this.mScaleStopPoint.mShape.graphics.setStrokeStyle(5 * this.getIconScale()).beginStroke('black');
        this.mScaleStopPoint.mShape.graphics.moveTo(startX, startY);
        this.mScaleStopPoint.mShape.graphics.lineTo(pointX, pointY);
        this.mScaleStopPoint.mShape.graphics.endStroke();
        this.mMainMapStage.addChild(this.mScaleStopPoint.mShape);
      }
    } else if (this.mIsDrawingZone || this.mIsDrawingSubLocation) {
      //Only need to draw the Zone if it has one or more points (With 1 point, you are drawing the Line between the point and where the cursor is)
      if (this.mTemporaryPerimeterPoints.length > 0) {
        //Get the information on the point where the mouse was moved on the Canvas
        const point = this.getPointFromStageXY(event.offsetX, event.offsetY);
        const pointX = this.translateMetersToPixels(point.x);
        const pointY = this.translateMetersToPixels(point.y);

        //If the area for the Temporary Zone has already been created... Delete it.  It will be recreated below with the new dimensions
        if (this.mTemporaryShape != null) {
          this.mMainMapStage.removeChild(this.mTemporaryShape);
          this.mTemporaryShape = null;
        }

        //Create variables to store information about the first and last points
        let startX: number = null, startY: number = null, lastX = null, lastY = null;

        //Cycle through the Points, drawing a line between each one and storing the first/last point values
        this.mTemporaryPerimeterPoints.forEach(ui => {
          const x = this.translateMetersToPixels(ui.mPoint.x);
          const y = this.translateMetersToPixels(ui.mPoint.y);
          if (this.mTemporaryShape == null) {
            this.mTemporaryShape = new createjs.Shape();
            this.mTemporaryShape.alpha = .5;
            this.mTemporaryShape.graphics.setStrokeStyle(1).beginStroke('black').beginFill(this.convertColorToHex(this.mIsDrawingZone? this.mSelectedZoneUi.mZone.color : this.mSelectedSubLocationUi.mSubLocation.color));
            this.mTemporaryShape.graphics.moveTo(x, y);
            startX = x;
            startY = y;
          } else {
            this.mTemporaryShape.graphics.lineTo(x, y);
          }
          lastX = x;
          lastY = y;
        });

        //Finish off the area by drawing a line to where the point is currently, then drawing the line back to the start point, filling in the area
        this.mTemporaryShape.graphics.lineTo(pointX, pointY);
        this.mTemporaryShape.graphics.lineTo(startX, startY);
        this.mTemporaryShape.graphics.endFill();
        this.mTemporaryShape.graphics.endStroke();

        //Draw another line, this time a bit thicker to represent the currently line being drawn
        this.mTemporaryShape.graphics.setStrokeStyle(5 * this.getIconScale()).beginStroke('black');
        this.mTemporaryShape.graphics.moveTo(lastX, lastY);
        this.mTemporaryShape.graphics.lineTo(pointX, pointY);
        this.mTemporaryShape.graphics.endStroke();

        //Add the Shape to the Main Map
        this.mMainMapStage.addChildAt(this.mTemporaryShape, 1);
      }
    }
  }

  onMouseUp(event: MouseEvent): void {
    //If the currentPos is null, then we are not processing a pan/drag move.
    if (this.mCurrentPos == null) {
      //Check to see if we are placing a selected Antenna/Reader/Asset/Device...
      if (this.mIsPlacing) {
        //Flip the 'mIsPlacing' flag to false to signal we are doing placing whatever is being placed
        this.mIsPlacing = false;

        //Set the 'mCancelClickEvent' flag to true to cancel clicking on anything that was just clicked on. (This gets set back to false on a "mousedown" event)
        this.mCancelClickEvent = true;

        //Get the Point for the Layer X/Y (Layer X/Y seems to be a rounded Stage X/Y values)
        const newPoint = this.getPointFromStageXY(event.offsetX, event.offsetY);

        //Depending on what is selected, set the position/location
        if (this.mSelectedAntennaUi != null) {
          this.mSelectedAntennaUi.mAntenna.locationInstance = this.mEditableLocation;
          this.mSelectedAntennaUi.mAntenna.position = newPoint;

          const index = this.mAntennasNotInLocation.indexOf(this.mSelectedAntennaUi);
          if (index > -1) {
            this.mAntennasInLocation.push(...this.mAntennasNotInLocation.splice(index, 1));
            this.drawAntenna(this.mSelectedAntennaUi);
          } else if (this.mSelectedAntennaUi.mIcon == null) {
            this.drawAntenna(this.mSelectedAntennaUi);
          } else {
            this.redrawAntenna(this.mSelectedAntennaUi);
          }
        } else if (this.mSelectedReaderUi != null) {
          this.mSelectedReaderUi.mReader.location = this.mEditableLocation;
          this.mSelectedReaderUi.mReader.position = newPoint;

          const index = this.mReadersNotInLocation.indexOf(this.mSelectedReaderUi);
          if (index > -1) {
            this.mReadersInLocation.push(...this.mReadersNotInLocation.splice(index, 1));
            this.drawReader(this.mSelectedReaderUi);
          } else if (this.mSelectedReaderUi.mIcon == null) {
            this.drawReader(this.mSelectedReaderUi);
          } else {
            this.redrawReader(this.mSelectedReaderUi);
          }
        } else if (this.mSelectedAssetUi != null) {
          this.mSelectedAssetUi.mObject.locationInstance = this.mEditableLocation;
          this.mSelectedAssetUi.mObject.position = newPoint;

          const index = this.mAssetsNotInLocation.indexOf(this.mSelectedAssetUi);
          if (index > -1) {
            this.mAssetsInLocation.push(...this.mAssetsNotInLocation.splice(index, 1));
            this.drawObject(this.mSelectedAssetUi);
          } else if (this.mSelectedAssetUi.mIcon == null) {
            this.drawObject(this.mSelectedAssetUi);
          } else {
            this.redrawObject(this.mSelectedAssetUi);
          }
        } else if (this.mSelectedDeviceUi != null) {
          this.mSelectedDeviceUi.mObject.locationInstance = this.mEditableLocation;
          this.mSelectedDeviceUi.mObject.position = newPoint;

          const index = this.mDevicesNotInLocation.indexOf(this.mSelectedDeviceUi);
          if (index > -1) {
            this.mDevicesInLocation.push(...this.mDevicesNotInLocation.splice(index, 1));
            this.drawObject(this.mSelectedDeviceUi);
          } else if (this.mSelectedDeviceUi.mIcon == null) {
            this.drawObject(this.mSelectedDeviceUi);
          } else {
            this.redrawObject(this.mSelectedDeviceUi);
          }
        }
      }
    } else {
      //mCurrentPos was not null, was processing a pan/drag move. Set to null to finish processing it.
      this.mCurrentPos = null;
    }
  }

  onMouseLeave(event: MouseEvent): void {
    this.mCurrentPos = null;
  }





  async refreshSiteLocations(): Promise<void> {
    try {
      this.mSiteLocations = await this.mLocationService.getAllRootLocationInstances();
    } catch (error) {
      this.handleError(error);
    }
  }





  siteLocationClicked(siteLocation: LocationInstance): void {
    console.log("Site Location Clicked! Location: " + (siteLocation == null? "*NULL*" : (siteLocation.id + "/" + siteLocation.name)));
    this.loadLocationAttempt(siteLocation);
  }

  async loadLocationAttempt(location: LocationInstance): Promise<void> {
    console.log("Attempting to Load Location: " + (location == null? "*NULL*" : (location.id + "/" + location.name)));
    if (!this.hasLocationOrAnythingInLocationChanged()) {
      //Current Location has no changes, no need to ask the User if they want to "Save Changes"/"Discard Changes"/"Go Back"... Just load the selected Location
      this.loadLocation(location);
    } else {
      //Current Location has changes, ask the User if they want to discard/save/cancel.  Get/Handle the response
      const userResponse = await this.mLocationChangesDialog.showAndWait();
      switch (userResponse) {
        case LocationChangeUserResponse.DISCARD: {
          this.loadLocation(location);
          return;
        }
        case LocationChangeUserResponse.SAVE: {
          try {
            await this.saveAllChanges();
            this.loadLocation(location);
          } catch (error) {
            this.handleError(error);
          }
          return;
        }
        case LocationChangeUserResponse.CANCEL: {
          return;
        }
      }
    }
  }

  loadLocation(siteLocation: LocationInstance): void {
    this.mLocationService
      .getLocationInstanceById(siteLocation.id)
      .then(
        location => {
          location.location = this.mLocationTypes.find(locationType => locationType.id == location.location.id);
          if (!location.color) {
            location.color = new Color();
            location.color.red = 0;
            location.color.blue = 0;
            location.color.green = 0;
          }
          if (!location.textColor) {
            location.textColor = new Color();
            location.textColor.red = 0;
            location.textColor.blue = 0;
            location.textColor.green = 0;
          }
          this.selectLocation(location);
        },
        error => this.handleError(error)
      );
  }

  hasLocationOrAnythingInLocationChanged(): boolean {
    return    this.isLocationChanged()
          ||  (this.mSubLocationUis != null && this.mSubLocationUis.some(ui => ui.isSubLocationChanged()))
          ||  (this.mZoneUis != null && this.mZoneUis.some(ui => ui.isZoneChanged()))
          ||  (this.mAntennasInLocation != null && this.mAntennasInLocation.some(ui => ui.isAntennaChanged()))
          ||  (this.mAntennasNotInLocation != null && this.mAntennasNotInLocation.some(ui => ui.isAntennaChanged()))
          ||  (this.mReadersInLocation != null && this.mReadersInLocation.some(ui => ui.isReaderChanged()))
          ||  (this.mReadersNotInLocation != null && this.mReadersNotInLocation.some(ui => ui.isReaderChanged()))
          ||  (this.mDevicesInLocation != null && this.mDevicesInLocation.some(ui => ui.isObjectChanged()))
          ||  (this.mDevicesNotInLocation != null && this.mDevicesNotInLocation.some(ui => ui.isObjectChanged()))
          ||  (this.mAssetsInLocation != null && this.mAssetsInLocation.some(ui => ui.isObjectChanged()))
          ||  (this.mAssetsNotInLocation != null && this.mAssetsNotInLocation.some(ui => ui.isObjectChanged()));
  }

  /*
   * Saves all the changes in the current Location.
   * If the Location is updated, and that Location is a Site Location... Then the Site Location list is refetched as well
   */
  async saveAllChanges(): Promise<boolean> {
    try {
      //If the Location has changed, update the Location
      if (this.isLocationChanged()) {
        const updatedLocation = await this.mLocationService.updateLocationInstance(this.mEditableLocation);
        this.mSelectedLocation = updatedLocation;
        this.mEditableLocation = JSON.parse(JSON.stringify(updatedLocation));

        //If this Location was in the SITES Tab, refresh that reference
        const siteLocation = this.mSiteLocations.find(location => location.id == updatedLocation.id);
        if (siteLocation != null) {
          this.refreshSiteLocations();
        }
      }

      //If any Sub Locations have changed, update the Sub Locations
      if (this.mSubLocationUis != null) {
        for (const ui of this.mSubLocationUis) {
          await this.saveSubLocationUi(ui);
        }
      }

      //If any Zones have changed, update the Zones
      if (this.mZoneUis != null) {
        for (const ui of this.mZoneUis) {
          await this.saveZoneUi(ui);
        }
      }

      //If any Antennas have changed, update the Antenna
      if (this.mAntennasInLocation != null) {
        for (const ui of this.mAntennasInLocation) {
          await this.saveAntennaUiPosition(ui);
        }
      }
      if (this.mAntennasNotInLocation != null) {
        for (const ui of this.mAntennasNotInLocation) {
          await this.saveAntennaUiPosition(ui);
        }
      }

      //If any Readers have changed, update the Readers
      if (this.mReadersInLocation != null) {
        for (const ui of this.mReadersInLocation) {
          await this.saveReaderUiPosition(ui);
        }
      }
      if (this.mReadersNotInLocation != null) {
        for (const ui of this.mReadersNotInLocation) {
          await this.saveReaderUiPosition(ui);
        }
      }

      //If any Devices have changed, update the Devices
      if (this.mDevicesInLocation != null) {
        for (const ui of this.mDevicesInLocation) {
          await this.saveObjectUiPosition(ui);
        }
      }
      if (this.mDevicesNotInLocation != null) {
        for (const ui of this.mDevicesNotInLocation) {
          await this.saveObjectUiPosition(ui);
        }
      }

      //If any Assets have changed, update the Assets
      if (this.mAssetsInLocation != null) {
        for (const ui of this.mAssetsInLocation) {
          await this.saveObjectUiPosition(ui);
        }
      }
      if (this.mAssetsNotInLocation != null) {
        for (const ui of this.mAssetsNotInLocation) {
          await this.saveObjectUiPosition(null);
        }
      }
    } catch (error) {
      this.handleError(error);

      //Error on one of the saves in the try/catch.  Return false indicating not everything was saved
      return false;
    }

    //If it made it this far, everything saved without any issues.  Return true indicating everything was saved correctly.
    return true;
  }

  async canDeactivate(): Promise<boolean> {
    if (!this.hasLocationOrAnythingInLocationChanged()) {
      //If there are no changes, allow the Navigation away from the Sites Page without any issues
      return true;
    } else {
      //There are changes, display the Dialog asking the User if they want to save/discard/cancel.  Get/Handle the response
      const userResponse = await this.mLocationChangesDialog.showAndWait();
      switch (userResponse) {
        case LocationChangeUserResponse.CANCEL: {
          return false;
        }
        case LocationChangeUserResponse.DISCARD: {
          return true;
        }
        case LocationChangeUserResponse.SAVE: {
          try {
            await this.saveAllChanges();
          } catch (error) {
            this.handleError(error);
            return false;
          }
          return true;
        }
      }
    }
  }





  selectLocation(location: LocationInstance): void {
    console.log("Selecting Location: " + (location == null? "*NULL*" : (location.id + "/" + location.name)));

    this.mAntennas = [];
    this.mAntennasInLocation.forEach(ui => this.eraseAntenna(ui));
    this.mAntennasInLocation = [];
    this.mAntennasNotInLocation = [];

    this.mReaders = [];
    this.mReadersInLocation.forEach(ui => this.eraseReader(ui));
    this.mReadersInLocation = [];
    this.mReadersNotInLocation = [];

    this.mDevices = [];
    this.mDevicesInLocation.forEach(ui => this.eraseObject(ui));
    this.mDevicesInLocation = [];
    this.mDevicesNotInLocation = [];

    this.mAssets = [];
    this.mAssetsInLocation.forEach(ui => this.eraseObject(ui));
    this.mAssetsInLocation = [];
    this.mAssetsNotInLocation = [];



    //If a Zone is selected, set the selected Zone to null so that the Perimeter Points get erased...
    if (this.mSelectedZoneUi != null) {
      this.selectZoneUi(null);
    }

    //If Zones were displayed in the Image, remove them
    this.mZoneUis.forEach(ui => this.eraseZone(ui));
    this.mZoneUis = [];



    this.mSubLocationUis.forEach(ui => this.eraseSubLocation(ui));
    this.mSubLocationUis = [];



    //Erase the old Location
    this.eraseLocation();

    //Set the Location as the selected Location
    this.mSelectedLocation = location;

    //Create a copy of the Location that is going to be in the Display and can be changed... Use mSelectedLocation to figure out if anything did change and it needs to be updated
    this.mEditableLocation = this.mSelectedLocation == null? null : JSON.parse(JSON.stringify(this.mSelectedLocation));

    if (this.mEditableLocation != null) {

      //Refresh the Scale Display on the Location Tab
      this.refreshLocationTabScaleDisplay();

      //Create the ZoneUis (These will get drawn after the Location has been drawn, but need to be created now)
      if (this.mEditableLocation.zones != null) {
        this.mEditableLocation.zones.forEach(zone => {
          const ui = new ZoneUi();

           if (!zone.color) {
            zone.color = new Color();
            zone.color.red = 255;
            zone.color.blue = 0;
            zone.color.green = 255;
          }
          if (!zone.textColor) {
            zone.textColor = new Color();
            zone.textColor.red = 0;
            zone.textColor.blue = 0;
            zone.textColor.green = 0;
          }
          ui.mZone = zone;
          ui.mZone.zone = this.mZoneTypes.find(zoneType => zoneType.id == ui.mZone.zone.id);
          ui.mOriginalZone = JSON.parse(JSON.stringify(zone));
          this.mZoneUis.push(ui);
        });
      }

      //Create the SubLocationUis (These will get drawn after the Location has been drawn, but need to be created now)
      if (this.mEditableLocation.childLocations != null) {
        this.mEditableLocation.childLocations.forEach(childLocation => {
          const ui = new SubLocationUi();
          if (!childLocation.color) {
            childLocation.color = new Color();
            childLocation.color.red = 0;
            childLocation.color.blue = 0;
            childLocation.color.green = 255;
          }
          if (!childLocation.textColor) {
            childLocation.textColor = new Color();
            childLocation.textColor.red = 0;
            childLocation.textColor.blue = 0;
            childLocation.textColor.green = 0;
          }
          ui.mSubLocation = childLocation;
          ui.mOriginalSubLocation = JSON.parse(JSON.stringify(childLocation));
          this.mSubLocationUis.push(ui);
        });
      }

      //Draw the Main/Mini Location Maps... This also refreshes the ZoneUis after it is done loading/rescaling/recentering...
      this.drawLocation();

      //Show the Zoom Slider
      $("#" + ZOOM_SLIDER_ID).fadeIn();
    }

    //Refresh the various Entity Instances
    this.refreshAntennas();
    this.refreshReaders();
    this.refreshDevices();
    this.refreshAssets();

    //Show the Location Tab after selecting a Location (Detect Changes so that the HREF on the Location Tab is there, so the JQuery show tab works below)
    this.cd.detectChanges();
    this.showLocationTab();
  }

  eraseLocation(): void {
    //Remove the old MiniMap image
    if (this.mMiniMapImage != null) {
      this.mMiniMapStage.removeChild(this.mMiniMapImage);
      this.mMiniMapImage = null;
      this.refreshMiniMapMeasurements();
    }

    //Remove the old Main image
    if (this.mMainMapImage != null) {
      this.mMainMapStage.removeChild(this.mMainMapImage);
      this.mMainMapImage = null;
    }
  }

  drawLocation(): void {
    //Erase the previous Location information so that it can be redrawn/recreated below
    this.eraseLocation();

    //If the selected Location exists, and there is an Image for it... Draw the Main Map and Mini Map
    if (this.mEditableLocation != null && this.mEditableLocation.image != null) {
      const component = this;
      //Draw the Mini Map
      {
          const image = document.createElement('img');
          image.crossOrigin = 'Anyonymous'; //Enable Cross Origin (Need to do it this way due to not being able to inside EaselJS itself - https://stackoverflow.com/questions/41468523/easeljs-cross-domain-images-workaround)
          image.src = this.mImageService.getImageSourceForImageId(this.mEditableLocation.image.id);

          this.mMiniMapImage = new createjs.Bitmap(image);

          this.mMiniMapImage.image.onload = function(): void {
            //Add the MiniMap Image to the MiniMap Stage
            component.mMiniMapStage.addChildAt(component.mMiniMapImage, 0);

            //Scale the Stage so the entire Image can be shown in the display
            let scale: number = null;
            if (component.mMiniMapImage.image.width > component.mMiniMapImage.image.height) {
              scale = component.mMiniMapSize / component.mMiniMapImage.image.width;
            } else {
              scale = component.mMiniMapSize / component.mMiniMapImage.image.height;
            }
            component.mMiniMapStage.scaleX = scale;
            component.mMiniMapStage.scaleY = scale;

            //Center the Image in the center of the MiniMap
            const diffX = component.mMiniMapSize - (component.mMiniMapStage.scaleX * component.mMiniMapImage.image.width);
            const diffY = component.mMiniMapSize - (component.mMiniMapStage.scaleY * component.mMiniMapImage.image.height);
            component.mMiniMapStage.x = diffX / 2;
            component.mMiniMapStage.y = diffY / 2;

            //Refresh the MiniMap Measurements based on the loaded Image
            component.refreshMiniMapMeasurements();
          };
      }

      //Draw the Main Map
      {
        const image = document.createElement('img');
        image.crossOrigin = 'Anyonymous'; //Enable Cross Origin (Need to do it this way due to not being able to inside EaselJS itself - https://stackoverflow.com/questions/41468523/easeljs-cross-domain-images-workaround)
        image.src = this.mImageService.getImageSourceForImageId(this.mEditableLocation.image.id);

        this.mMainMapImage = new createjs.Bitmap(image);

        this.mMainMapImage.image.onload = function(): void {
          //Add the Main Map Image to the Main Map Stage
          component.mMainMapStage.addChildAt(component.mMainMapImage, 0);

          //Scale the Stage so that the entire Image can be shown in the display
          const wScale = component.mMainCanvasWidthInPixels / component.mMainMapImage.image.width;
          const hScale = component.mMainCanvasHeightInPixels / component.mMainMapImage.image.height;

          const scaleIndex = Math.floor((wScale > hScale? hScale : wScale) / SCALE_INDEX_MULTIPLIER);
          component.setMainMapScaleIndex(scaleIndex);

          //Center the Image in the center of the Main Map
          const diffX = component.mMainCanvasWidthInPixels - (component.mMainMapStage.scaleX * component.mMainMapImage.image.width);
          const diffY = component.mMainCanvasHeightInPixels - (component.mMainMapStage.scaleY * component.mMainMapImage.image.height);
          component.mMainMapStage.x = diffX / 2;
          component.mMainMapStage.y = diffY / 2;

          //Redraw the Zones on the new Location Map to account for the new scaling/panning values
          component.mZoneUis.forEach(ui => component.drawZone(ui));

          //Redraw the Sub Locations on the new Location Map to account for the new scaling/panning values
          component.mSubLocationUis.forEach(ui => component.drawSubLocation(ui));
        }
      }
    }
  }





  setMainMapScaleIndex(scaleIndex: number): void {
    if (scaleIndex < MIN_SCALE_INDEX) {
      scaleIndex = MIN_SCALE_INDEX;
    } else if (scaleIndex > MAX_SCALE_INDEX) {
      scaleIndex = MAX_SCALE_INDEX;
    }

    if (this.mMainMapScaleIndex != scaleIndex) {
      console.log("Changing Scale: " + this.mMainMapScaleIndex + " => " + scaleIndex);
      this.mMainMapScaleIndex = scaleIndex;
      const newScale = scaleIndex * SCALE_INDEX_MULTIPLIER;
      this.mMainMapStage.scaleX = newScale;
      this.mMainMapStage.scaleY = newScale;

      //Redraw the starting point used for scaling (if it exists)
      if (this.mScaleStartPoint != null) {
        this.drawPerimeterPoint(this.mScaleStartPoint);
      }

      //Redraw any Temporary Zone that is being drawn
      this.redrawTemporaryZonePoints();

      //Redraw the selected Sub Location's Perimeter Points if needed (Circles for the Perimter Points inversely scaled based on the main scaling so they always appear the same size)
      if (this.mSelectedSubLocationUi != null) {
        this.redrawSubLocationPerimeterPointsAndName(this.mSelectedSubLocationUi);
      }

      //Redraw the selected Zone's Perimeter Points if needed (Circles for the Perimeter Points inversely scaled based on the main scaling so that they always appear the same size)
      if (this.mSelectedZoneUi != null) {
        this.redrawZonePerimeterPointsAndName(this.mSelectedZoneUi);
      }

      //Redraw any Antenna Icons/Highlights that are currently displayed in the Location
      if (this.mAntennasInLocation != null) {
        this.mAntennasInLocation.forEach(ui => this.redrawAntenna(ui));
      }

      //Redraw any Reader Icons that are currently displayed in the Location
      if (this.mReadersInLocation != null) {
        this.mReadersInLocation.forEach(ui => this.redrawReader(ui));
      }

      //Redraw any Asset Icons that are currently displayed in the Location
      if (this.mAssetsInLocation != null) {
        this.mAssetsInLocation.forEach(ui => this.redrawObject(ui));
      }

      //Redraw any Device Icons that are currently displayed in the Location
      if (this.mDevicesInLocation != null) {
        this.mDevicesInLocation.forEach(ui => this.redrawObject(ui));
      }
    }
  }

  eraseSubLocation(ui: SubLocationUi): void {
    if (ui.mShape != null) {
      this.mMainMapStage.removeChild(ui.mShape);
      ui.mShape = null;
    }
  }

  drawSubLocation(ui: SubLocationUi): void {
    //Erase the current Sub Location from the Map so that it can be redrawn
    this.eraseSubLocation(ui);

    //Create the createjs.Shape used to display the Sub Location on the Map
    ui.mShape = new createjs.Shape();
    ui.mShape.alpha = .5;
    ui.mShape.graphics.setStrokeStyle(1).beginStroke("black").beginFill(this.convertColorToHex(ui.mSubLocation.color));
    let startX: number = null, startY: number = null;
    ui.mSubLocation.perimeterPoints.forEach(point => {
      const x = this.translateMetersToPixels(point.x);
      const y = this.translateMetersToPixels(point.y);
      if (startX == null) {
        ui.mShape.graphics.moveTo(x, y);
        startX = x;
        startY = y;
      } else {
        ui.mShape.graphics.lineTo(x, y);
      }
    });
    ui.mShape.graphics.lineTo(startX, startY);
    ui.mShape.graphics.endFill();

    //Add the Sub Location to the Map
    this.mMainMapStage.addChildAt(ui.mShape, this.mMainMapStage.numChildren == 0? 0 : 1);

    //Add a "click" listener to the Icon to select the clicked on Sub Location
    const component = this;
    ui.mShape.addEventListener("click", function(event: createjs.MouseEvent): void {
      console.log("Clicked on Sub Location: " + ui.mSubLocation.name + " - Canceled? " + component.mCancelClickEvent);
      if (component.areThingsSelectable()) {
        if (!component.mCancelClickEvent) {
          if (ui == component.mSelectedSubLocationUi) {
            component.loadLocationAttempt(ui.mSubLocation);
          } else {
            component.selectSubLocationUi(ui);
          }
        }
        event.stopPropagation();
      }
    });
  }

  eraseZone(ui: ZoneUi): void {
    if (ui.mShape != null) {
      this.mMainMapStage.removeChild(ui.mShape);
      ui.mShape = null;
    }
  }

  drawZone(ui: ZoneUi): void {
    //Erase the current Zone from the Map so that it can be redrawn
    this.eraseZone(ui);

    //Create the createjs.Shape used to display the Zone on the Map
    ui.mShape = new createjs.Shape();
    ui.mShape.alpha = .5;
    ui.mShape.graphics.setStrokeStyle(1).beginStroke("black").beginFill(this.convertColorToHex(ui.mZone.color));
    let startX: number = null, startY: number = null;
    ui.mZone.perimeterPoints.forEach(point => {
      const x = this.translateMetersToPixels(point.x);
      const y = this.translateMetersToPixels(point.y);
      if (startX == null) {
        ui.mShape.graphics.moveTo(x, y);
        startX = x;
        startY = y;
      } else {
        ui.mShape.graphics.lineTo(x, y);
      }
    });
    ui.mShape.graphics.lineTo(startX, startY);
    ui.mShape.graphics.endFill();

    //Add the Zone to the Map
    this.mMainMapStage.addChildAt(ui.mShape, this.mMainMapStage.numChildren == 0? 0 : 1);

    //Add a "click" listener to the Icon to select the clicked on zone
    const component = this;
    ui.mShape.addEventListener("click", function(event: createjs.MouseEvent): void {
      console.log("Clicked on Zone: " + ui.mZone.name + " - Canceled? " + component.mCancelClickEvent);
      if (component.areThingsSelectable()) {
        if (!component.mCancelClickEvent) {
          if (ui == component.mSelectedZoneUi) {
            component.selectZoneUi(null);
          } else {
            component.selectZoneUi(ui);
          }
        }
        event.stopPropagation();
      }
    });
  }





  refreshMiniMapMeasurements(): void {
    console.log("Refreshing MiniMap and Scale...");

    //Refresh the MiniMap Measurements
    if (this.mEditableLocation == null || this.mEditableLocation.scale == null || this.mMiniMapImage == null || this.mMiniMapImage.image == null) {
      this.mMiniMapHeightDisplay = DEFAULT_UNKNOWN;
      this.mMiniMapWidthDisplay = DEFAULT_UNKNOWN;
    } else {
      //Get the scale value for the Location (Pixels Per Meter)
      const scale = this.mEditableLocation.scale;

      //Figure out the Width Display
      if (this.mMiniMapImage.image.width == null) {
        this.mMiniMapWidthDisplay = DEFAULT_UNKNOWN;
      } else {
        this.mMiniMapWidthDisplay = this.getMiniMapMeasurementDisplay(this.mMiniMapImage.image.width, scale);
      }

      //Figure out the Height Display
      if (this.mMiniMapImage.image.height == null) {
        this.mMiniMapHeightDisplay = DEFAULT_UNKNOWN;
      } else {
        this.mMiniMapHeightDisplay = this.getMiniMapMeasurementDisplay(this.mMiniMapImage.image.height, scale);
      }
    }
  }

  refreshLocationTabScaleDisplay(): void {
    if (this.mEditableLocation == null || this.mEditableLocation.scale == null) {
      console.log("Defaulting Scale for Location: " + this.mEditableLocation.name);
      this.mLocationScaleDisplay = DEFAULT_UNKNOWN;
    } else {
      console.log("Figuring out scale for Location: " + this.mEditableLocation.name);
      const pixelsPerMeter = this.mEditableLocation.scale;
      const pixelsPerFoot = pixelsPerMeter * FEET_TO_METERS_MULTIPLIER;
      const roundedValue = Math.round(pixelsPerFoot * 100) / 100;
      this.mLocationScaleDisplay = roundedValue.toFixed(2) + " pixels : 1 foot";
    }
  }

  getMiniMapMeasurementDisplay(pixels: number, pixelsToMeterScale: number): string {
    //Convert Pixels => Meters
    let size = (pixels / pixelsToMeterScale);

    //Convert the Measurement (in metric / meters) to a human-readable string
    return DisplayModule.getDisplayForMeasurement(size);
  }





  getPointDisplay(point: Point): string {
    return DisplayModule.getDisplayForPoint(point);
  }

  translateMetersToPixels(posVal: number): number {
    if (this.mEditableLocation == null || this.mEditableLocation.scale == null) {
      return posVal;
    } else {
      return posVal * this.mEditableLocation.scale;
    }
  }

  translatePixelsToMeters(pixelVal: number): number {
    if (this.mEditableLocation == null || this.mEditableLocation.scale == null) {
      return pixelVal;
    } else {
      return pixelVal / this.mEditableLocation.scale;
    }
  }





  isLocationChanged(): boolean {
    if (this.mSelectedLocation != null) {
      return  !(
              this.mSelectedLocation.name == this.mEditableLocation.name
        &&    this.mSelectedLocation.location.id == this.mEditableLocation.location.id
        &&    this.mSelectedLocation.description == this.mEditableLocation.description
        &&    this.mSelectedLocation.scale == this.mEditableLocation.scale
        &&    this.isSameImage(this.mSelectedLocation.image, this.mEditableLocation.image)
      );
    }
    return false;
  }

  isSameImage(image1: Image, image2: Image): boolean {
    if (image1 == null && image2 == null) {
      return true;
    } else if (image1 == null || image2 == null) {
      return false;
    } else {
      return image1.id == image2.id;
    }
  }

  async updateLocationClicked(): Promise<void> {
    console.log("Update Location Clicked! Is Changed? " + this.isLocationChanged());
    if (this.isLocationChanged()) {
      try {
        const updatedLocation = await this.mLocationService.updateLocationInstance(this.mEditableLocation);

        //If this Location was in the SITES Tab, refresh that reference
        if (this.mSiteLocations.find(location => location.id == updatedLocation.id) != null) {
          this.refreshSiteLocations();
        }

        //Select the updated Location
        this.selectLocation(updatedLocation);
      } catch (error) {
        this.handleError(error);
      }
    }
  }





  selectSubLocationUi(ui: SubLocationUi): void {
    console.log("selectSubLocationUi() - {}", ui);
    //Cancel Drawing a Sub Location
    if (this.mIsDrawingSubLocation) {
      this.cancelDrawingSubLocation();
      return;
    } else if (this.mSelectedSubLocationUi != ui) {
      //Handle a Sub Location Change
      console.log("Selected Sub Location Changing: " + (this.mSelectedSubLocationUi == null? "*NULL*" : this.mSelectedSubLocationUi.mSubLocation.id + "/" + this.mSelectedSubLocationUi.mSubLocation.name) + " => " + (ui == null? "*NULL*" : ui.mSubLocation.id + "/" + ui.mSubLocation.name) + " - Drawing Sub Location: " + this.mIsDrawingSubLocation);
      //Set the mIsPlacing Flag to false to exit out of the "Placing" mode
      this.mIsPlacing = false;

      //Reset the Sub Location Color
      this.mSelectedSubLocationUiColor = null;

      //Remove the Perimeter Points drawn for the old selected Sub Location (if one was selected)
      this.eraseSubLocationPerimeterPointsAndName(this.mSelectedSubLocationUi);

      //Set the passed in UI as the new selected Sub Location
      this.mSelectedSubLocationUi = ui;

      if (this.mSelectedSubLocationUi != null) {
        console.warn("selectSublocationUi");
        //Set the Sub Location Color to the one for the SubLocationUi that was selected
        this.mSelectedSubLocationUiColor = this.rgbToHex(ui.mSubLocation.color.red, ui.mSubLocation.color.green, ui.mSubLocation.color.blue);
        if (ui.mSubLocation.textColor) {
          this.mSelectedSubLocationUiTextColor = this.rgbToHex(ui.mSubLocation.textColor.red, ui.mSubLocation.textColor.green, ui.mSubLocation.textColor.blue);
        } else {
          this.mSelectedSubLocationUiTextColor = this.rgbToHex(0, 0, 0);

        }
        //Draw the Perimeter Points on the canvas so the User can adjust the zone if desired/needed
        this.drawSubLocationPerimeterPointsAndName(this.mSelectedSubLocationUi);

        //Show the "SUBLOCATIONS" Tab
        this.showSubLocationsTab();
      }
    }
  }

  selectZoneUi(ui: ZoneUi): void {
    //Cancel Drawing a Zone
    if (this.mIsDrawingZone) {
      this.cancelDrawingZone();
      return;
    } else if (this.mSelectedZoneUi != ui) {
      //Handle a Zone Change
      console.log("Selected Zone Changing: " + (this.mSelectedZoneUi == null? "*NULL*" : this.mSelectedZoneUi.mZone.id + "/" + this.mSelectedZoneUi.mZone.name) + " => " + (ui == null? "*NULL*" : ui.mZone.id + "/" + ui.mZone.name) + " - Drawing Zone: " + this.mIsDrawingZone);
      //Set the mIsPlacing Flag to false to exit out of the "Placing" mode
      this.mIsPlacing = false;

      //Reset the Zone Color
      this.mSelectedZoneUiColor = null;
      this.mSelectedZoneUiTextColor = null;

      //Remove the Perimeter Points drawn for the old selected Zone (if one was selected)
      this.eraseZonePerimeterPointsAndName(this.mSelectedZoneUi);

      //Set the passed in UI as the new selected Zone
      this.mSelectedZoneUi = ui;

      if (this.mSelectedZoneUi != null) {
        //Set the Zone Color to the one for the ZoneUi that was selected
        if(ui.mZone.color) {
          this.mSelectedZoneUiColor = this.rgbToHex(ui.mZone.color.red, ui.mZone.color.green, ui.mZone.color.blue);
        } else {
          this.mSelectedZoneUiColor = this.rgbToHex(0, 0, 0);
        }
        if (ui.mZone.textColor) {
          this.mSelectedZoneUiTextColor = this.rgbToHex(ui.mZone.textColor.red, ui.mZone.textColor.green, ui.mZone.textColor.blue);
        } else {
          this.mSelectedZoneUiTextColor = this.rgbToHex(0, 0, 0);
        }
        //Draw the Perimeter Points on the canvas so the User can adjust the zone if desired/needed
        this.drawZonePerimeterPointsAndName(this.mSelectedZoneUi);

        //Show the "ZONES" Tab
        this.showZonesTab();
      }
    }
  }





  zoneNameChanged(): void {
    if (this.mSelectedZoneUi != null) {
      this.redrawZonePerimeterPointsAndName(this.mSelectedZoneUi);
    }
  }

  subLocationNameChanged(): void {
    if (this.mSelectedSubLocationUi != null) {
      this.redrawSubLocationPerimeterPointsAndName(this.mSelectedSubLocationUi);
    }
  }





  redrawSubLocationPerimeterPointsAndName(ui: SubLocationUi): void {
    if (ui != null) {
      //Redraw the Perimeter Points and Name
      if (ui.mPerimeterPointUis != null) {

        //Variables to hold the Min/Max X/Y values... Used to figure out where to draw/place the Sub Location Name
        let minX, maxX, minY, maxY: number = null;

        ui.mPerimeterPointUis.forEach(pointUi => {
          const x = this.translateMetersToPixels(pointUi.mPoint.x);
          const y = this.translateMetersToPixels(pointUi.mPoint.y);

          //Store the Min/Max X/Y values so that the Sub Location Name can be drawn in the middle of the points
          if (minX == null) {
            minX = x;
            maxX = x;
            minY = y;
            maxY = y;
          } else {
            if (x < minX) {
              minX = x;
            } else if (x > maxX) {
              maxX = x;
            }
            if (y < minY) {
              minY = y;
            } else if (y > maxY) {
              maxY = y;
            }
          }

          //Redraw the Perimeter Point
          pointUi.mShape.graphics.clear();
          pointUi.mShape.graphics.beginFill('green').drawCircle(x, y, 10 * this.getIconScale());
        });

        //Draw on the Sub Location of the Zone in the middle of all the perimeter points
        if (minX != null) {
          const iconScale = this.getIconScale();

          const midX = minX + ((maxX - minX) / 2);
          const midY = minY + ((maxY - minY) / 2);
          ui.mNameField.text = ui.mSubLocation.name;
          ui.mNameField.scaleX = iconScale;
          ui.mNameField.scaleY = iconScale;
          ui.mNameField.x = midX - ((ui.mNameField.getMeasuredWidth() * iconScale) / 2);
          ui.mNameField.y = midY - ((ui.mNameField.getMeasuredHeight() * iconScale) / 2);
        }
      }
    }
  }

  eraseSubLocationPerimeterPointsAndName(ui: SubLocationUi): void {
    if (ui != null) {
      //Remove the Name of the Sub Location
      if (ui.mNameField != null) {
        this.mMainMapStage.removeChild(ui.mNameField);
        ui.mNameField = null;
      }

      //Remove the Perimeter Points
      if (ui.mPerimeterPointUis != null) {
        ui.mPerimeterPointUis.forEach(pointUi => {
          if (pointUi.mShape != null) {
            this.mMainMapStage.removeChild(pointUi.mShape);
            pointUi.mShape = null;
          }
        });
        ui.mPerimeterPointUis = null;
      }
    }
  }





  redrawZonePerimeterPointsAndName(ui: ZoneUi): void {
    if (ui != null) {
      //Redraw the Perimeter Points and Name
      if (ui.mPerimeterPointUis != null) {

        //Variables to hold the Min/Max X/Y values... Used to figure out where to draw/place the Zone Name
        let minX, maxX, minY, maxY: number = null;

        ui.mPerimeterPointUis.forEach(pointUi => {
          const x = this.translateMetersToPixels(pointUi.mPoint.x);
          const y = this.translateMetersToPixels(pointUi.mPoint.y);

          //Store the Min/Max X/Y values so that the Zone Name can be drawn in the middle of the points
          if (minX == null) {
            minX = x;
            maxX = x;
            minY = y;
            maxY = y;
          } else {
            if (x < minX) {
              minX = x;
            } else if (x > maxX) {
              maxX = x;
            }
            if (y < minY) {
              minY = y;
            } else if (y > maxY) {
              maxY = y;
            }
          }

          //Redraw the Perimeter Point
          pointUi.mShape.graphics.clear();
          pointUi.mShape.graphics.beginFill('green').drawCircle(x, y, 10 * this.getIconScale());
        });

        //Draw on the Name of the Zone in the middle of all the perimeter points
        if (minX != null) {
          const iconScale = this.getIconScale();

          const midX = minX + ((maxX - minX) / 2);
          const midY = minY + ((maxY - minY) / 2);
          ui.mNameField.text = ui.mZone.name;
          ui.mNameField.scaleX = iconScale;
          ui.mNameField.scaleY = iconScale;
          ui.mNameField.x = midX - ((ui.mNameField.getMeasuredWidth() * iconScale) / 2);
          ui.mNameField.y = midY - ((ui.mNameField.getMeasuredHeight() * iconScale) / 2);
        }
      }
    }
  }

  eraseZonePerimeterPointsAndName(ui: ZoneUi): void {
    if (ui != null) {
      //Remove the Name of the Zone
      if (ui.mNameField != null) {
        this.mMainMapStage.removeChild(ui.mNameField);
        ui.mNameField = null;
      }

      //Remove the Perimeter Points
      if (ui.mPerimeterPointUis != null) {
        ui.mPerimeterPointUis.forEach(pointUi => {
          if (pointUi.mShape != null) {
            this.mMainMapStage.removeChild(pointUi.mShape);
            pointUi.mShape = null;
          }
        });
        ui.mPerimeterPointUis = null;
      }
    }
  }

  erasePerimeterPoint(ui: PerimeterPointUi): void {
    if (ui.mShape != null) {
      this.mMainMapStage.removeChild(ui.mShape);
      ui.mShape = null;
    }
  }

  drawPerimeterPoint(ui: PerimeterPointUi): void {
    //Erase any existing UI from the screen before drawing a new one
    this.erasePerimeterPoint(ui);

    //Convert the Point's X/Y values to the Pixels
    const x = this.translateMetersToPixels(ui.mPoint.x);
    const y = this.translateMetersToPixels(ui.mPoint.y);

    //Draw a green circle representing the Perimeter Point
    ui.mShape = new createjs.Shape();
    ui.mShape.graphics.beginFill('green').drawCircle(x, y, 10 * this.getIconScale());
    this.mMainMapStage.addChild(ui.mShape);
  }

  drawZonePerimeterPointsAndName(ui: ZoneUi): void {
    if (ui.mZone.perimeterPoints != null) {
      //Variables to hold the Min/Max X/Y values... Used to figure out where to draw/place the Zone Name
      let minX, maxX, minY, maxY: number = null;

      ui.mPerimeterPointUis = [];
      ui.mZone.perimeterPoints.forEach(perimeterPoint => {
        //Convert the Point's X/Y values to the Pixels
        const x = this.translateMetersToPixels(perimeterPoint.x);
        const y = this.translateMetersToPixels(perimeterPoint.y);

        //Store the Min/Max X/Y values so that the Zone Name can be drawn in the middle of the points
        if (minX == null) {
          minX = x;
          maxX = x;
          minY = y;
          maxY = y;
        } else {
          if (x < minX) {
            minX = x;
          } else if (x > maxX) {
            maxX = x;
          }
          if (y < minY) {
            minY = y;
          } else if (y > maxY) {
            maxY = y;
          }
        }

        //Draw a green circle that will always appear to have a 10 pixel radius no matter the scale, this is what represents the point
        const pointUi = new PerimeterPointUi();
        pointUi.mPoint = perimeterPoint;
        ui.mPerimeterPointUis.push(pointUi);
        this.drawPerimeterPoint(pointUi);

        //Add an Event Listener to the circle to support adjusting the perimeter points for the Zone
        const component = this;
        pointUi.mShape.addEventListener("pressmove", function(event: createjs.MouseEvent): void {
          //Get the current Scale applied to the Main Map (Inverse it so that it can reverse the scaling of the values)
          const iconScale = component.getIconScale();

          //Scale the Stage X/Y values
          const scaledStageX = (event.stageX) * iconScale;
          const scaledStageY = (event.stageY) * iconScale;

          //Scale the Pan X/Y values
          const scaledPanX = (component.mMainMapStage.x) * iconScale;
          const scaledPanY = (component.mMainMapStage.y) * iconScale;

          //Get the Point's new X/Y values in Pixels
          const newXPixels = scaledStageX - scaledPanX;
          const newYPixels = scaledStageY - scaledPanY;

          //Convert the Point's new X/Y values to Meters
          const newXMeters = component.translatePixelsToMeters(newXPixels);
          const newYMeters = component.translatePixelsToMeters(newYPixels);


          //Set the Point's new X/Y value in meters
          pointUi.mPoint.x = newXMeters;
          pointUi.mPoint.y = newYMeters;

          //Redraw the Point's circle so that it appears to be moving with the drag
          pointUi.mShape.graphics.clear();
          pointUi.mShape.graphics.beginFill('green').drawCircle(newXPixels, newYPixels, 10 * iconScale);

          //Redraw the Zone's Area
          component.drawZone(ui);
        });

        pointUi.mShape.addEventListener("click", function(event: createjs.MouseEvent): void {
          event.stopPropagation();
        });

      });

      //Draw on the Name of the Zone in the middle of all the perimeter points
      if (minX != null) {
        const iconScale = this.getIconScale();

        const midX = minX + ((maxX - minX) / 2);
        const midY = minY + ((maxY - minY) / 2);

        const zoneNameColor = (ui.mZone.textColor) ?
          this.rgbToHex(ui.mZone.textColor.red, ui.mZone.textColor.green, ui.mZone.textColor.blue)
          : "#000000";

        ui.mNameField = new createjs.Text(ui.mZone.name, "bold 16px Arial", zoneNameColor);
        ui.mNameField.scaleX = iconScale;
        ui.mNameField.scaleY = iconScale;
        ui.mNameField.x = midX - ((ui.mNameField.getMeasuredWidth() * iconScale) / 2);
        ui.mNameField.y = midY - ((ui.mNameField.getMeasuredHeight() * iconScale) / 2);
        this.mMainMapStage.addChild(ui.mNameField);
      }
    }
  }





  drawSubLocationPerimeterPointsAndName(ui: SubLocationUi): void {
    if (ui.mSubLocation.perimeterPoints != null) {
      //Variables to hold the Min/Max X/Y values... Used to figure out where to draw/place the Zone Name
      let minX, maxX, minY, maxY: number = null;

      ui.mPerimeterPointUis = [];
      ui.mSubLocation.perimeterPoints.forEach(perimeterPoint => {
        //Convert the Point's X/Y values to the Pixels
        const x = this.translateMetersToPixels(perimeterPoint.x);
        const y = this.translateMetersToPixels(perimeterPoint.y);

        //Store the Min/Max X/Y values so that the Zone Name can be drawn in the middle of the points
        if (minX == null) {
          minX = x;
          maxX = x;
          minY = y;
          maxY = y;
        } else {
          if (x < minX) {
            minX = x;
          } else if (x > maxX) {
            maxX = x;
          }
          if (y < minY) {
            minY = y;
          } else if (y > maxY) {
            maxY = y;
          }
        }

        //Draw a green circle that will always appear to have a 10 pixel radius no matter the scale, this is what represents the point
        const pointUi = new PerimeterPointUi();
        pointUi.mPoint = perimeterPoint;
        ui.mPerimeterPointUis.push(pointUi);
        this.drawPerimeterPoint(pointUi);

        //Add an Event Listener to the circle to support adjusting the perimeter points for the Zone
        const component = this;
        pointUi.mShape.addEventListener("pressmove", function(event: createjs.MouseEvent): void {
          //Get the current Scale applied to the Main Map (Inverse it so that it can reverse the scaling of the values)
          const iconScale = component.getIconScale();

          //Scale the Stage X/Y values
          const scaledStageX = (event.stageX) * iconScale;
          const scaledStageY = (event.stageY) * iconScale;

          //Scale the Pan X/Y values
          const scaledPanX = (component.mMainMapStage.x) * iconScale;
          const scaledPanY = (component.mMainMapStage.y) * iconScale;

          //Get the Point's new X/Y values in Pixels
          const newXPixels = scaledStageX - scaledPanX;
          const newYPixels = scaledStageY - scaledPanY;

          //Convert the Point's new X/Y values to Meters
          const newXMeters = component.translatePixelsToMeters(newXPixels);
          const newYMeters = component.translatePixelsToMeters(newYPixels);


          //Set the Point's new X/Y value in meters
          pointUi.mPoint.x = newXMeters;
          pointUi.mPoint.y = newYMeters;

          //Redraw the Point's circle so that it appears to be moving with the drag
          pointUi.mShape.graphics.clear();
          pointUi.mShape.graphics.beginFill('green').drawCircle(newXPixels, newYPixels, 10 * iconScale);

          //Redraw the Sub Location's Area
          component.drawSubLocation(ui);
        });

        pointUi.mShape.addEventListener("click", function(event: createjs.MouseEvent): void {
          event.stopPropagation();
        });

      });

      //Draw on the Name of the Zone in the middle of all the perimeter points
      if (minX != null) {
        const iconScale = this.getIconScale();

        const midX = minX + ((maxX - minX) / 2);
        const midY = minY + ((maxY - minY) / 2);
        const sublocationNameColor = (ui.mSubLocation.textColor) ?
          this.rgbToHex(ui.mSubLocation.textColor.red, ui.mSubLocation.textColor.green, ui.mSubLocation.textColor.blue)
          : "#000000";

        ui.mNameField = new createjs.Text(ui.mSubLocation.name, "bold 16px Arial", sublocationNameColor);
        ui.mNameField.scaleX = iconScale;
        ui.mNameField.scaleY = iconScale;
        ui.mNameField.x = midX - ((ui.mNameField.getMeasuredWidth() * iconScale) / 2);
        ui.mNameField.y = midY - ((ui.mNameField.getMeasuredHeight() * iconScale) / 2);
        this.mMainMapStage.addChild(ui.mNameField);
      }
    }
  }





  subLocationColorChanged(): void {
    //Set the selected SubLocationUi's color to the new value
    this.mSelectedSubLocationUi.mSubLocation.color = this.convertHexToColor(this.mSelectedSubLocationUiColor);

    //Need to refresh the display on the main floorplan image, just add the SubLocationUi to the Map again (there is logic in there to remove the old Image)
    this.drawSubLocation(this.mSelectedSubLocationUi);
  }
  subLocationTextColorChanged(): void {
    //Set the selected SubLocationUi's text color to the new value
    this.mSelectedSubLocationUi.mSubLocation.textColor = this.convertHexToColor(this.mSelectedSubLocationUiTextColor);


    //Remove the Perimeter Points drawn for the old selected Zone (if one was selected)
    this.eraseSubLocationPerimeterPointsAndName(this.mSelectedSubLocationUi);

    //Need to refresh the display on the main floorplan image, just add the SubLocationUi to the Map again (there is logic in there to remove the old Image)
    this.drawSubLocationPerimeterPointsAndName(this.mSelectedSubLocationUi);

  }
  zoneColorChanged(): void {
    //Set the selected ZoneUi's color to the new value
    this.mSelectedZoneUi.mZone.color = this.convertHexToColor(this.mSelectedZoneUiColor);

    //Need to refresh the display on the main floorplan image, just add the ZoneUi to the Map again (there is logic in there to remove the old Image)
    this.drawZone(this.mSelectedZoneUi);
  }
  zoneTextColorChanged(): void {
    console.warn("zoneTextColorChanged()");
    //Set the selected ZoneUi's text color to the new value
    this.mSelectedZoneUi.mZone.textColor = this.convertHexToColor(this.mSelectedZoneUiTextColor);

    //Remove the Perimeter Points drawn for the old selected Zone (if one was selected)
    this.eraseZonePerimeterPointsAndName(this.mSelectedZoneUi);

    //Need to refresh the display on the main floorplan image, just add the ZoneUi to the Map again (there is logic in there to remove the old Image)
    this.drawZonePerimeterPointsAndName(this.mSelectedZoneUi);
  }




  deleteSubLocationClicked(): void {
    console.log("Delete Zone Clicked!");
    if (this.mSelectedSubLocationUi.isSubLocationNew()) {
      //Erase the Sub Location from the Main Map
      if (this.mSelectedSubLocationUi.mShape != null) {
        this.mMainMapStage.removeChild(this.mSelectedSubLocationUi.mShape);
      }
      this.eraseSubLocationPerimeterPointsAndName(this.mSelectedSubLocationUi);

      //Remove the Sub Location from the mZoneUis array
      this.mSubLocationUis.splice(this.mSubLocationUis.indexOf(this.mSelectedSubLocationUi), 1);

      //Set the selected Sub Location to null
      this.selectSubLocationUi(null);
    } else {
      //This is an existing Sub Location... Call the Service to try to delete it
      this.mLocationService
        .deleteLocationInstances([this.mSelectedSubLocationUi.mSubLocation.id])
        .then(
          success => {
            //Erase the Zone from the Main Map
            if (this.mSelectedSubLocationUi.mShape != null) {
              this.mMainMapStage.removeChild(this.mSelectedSubLocationUi.mShape);
            }
            this.eraseSubLocationPerimeterPointsAndName(this.mSelectedSubLocationUi);

            //Remove the Sub Location from the mZoneUis array
            this.mSubLocationUis.splice(this.mSubLocationUis.indexOf(this.mSelectedSubLocationUi), 1);

            //Set the selected Sub Location to null
            this.selectSubLocationUi(null);
          },
          error => this.handleError(error)
        );
    }
  }
  async updateSubLocationClicked(): Promise<void> {
    console.log("Update Sub Location Clicked! Changed? " + this.mSelectedSubLocationUi.isSubLocationChanged());
    if (this.mSelectedSubLocationUi.isSubLocationChanged() && !this.mIsDrawingSubLocation) {
      try {
        await this.saveSubLocationUi(this.mSelectedSubLocationUi);
      } catch (error) {
        this.handleError(error);
      }
    }
  }

  async saveSubLocationUi(ui: SubLocationUi): Promise<void> {
    if (ui != null) {
      if (ui.isSubLocationNew()) {
        const savedSubLocation = await this.mLocationService.createLocationInstance(ui.mSubLocation);
        ui.mOriginalSubLocation = savedSubLocation;
        ui.mSubLocation = JSON.parse(JSON.stringify(savedSubLocation));
      } else if (ui.isSubLocationChanged()) {
        const savedSubLocation = await this.mLocationService.updateLocationInstance(ui.mSubLocation);
        ui.mOriginalSubLocation = savedSubLocation;
        ui.mSubLocation = JSON.parse(JSON.stringify(savedSubLocation));
      }
    }
  }

  async createSubLocationClicked(): Promise<void> {
    console.log("Create Sub Location Clicked!");
    if (this.mSelectedSubLocationUi.isSubLocationNew() && !this.mIsDrawingSubLocation) {
      try {
        await this.saveSubLocationUi(this.mSelectedSubLocationUi);
      } catch (error) {
        this.handleError(error);
      }
    }
  }





  deleteZoneClicked(): void {
    console.log("Delete Zone Clicked!");
    if (this.mSelectedZoneUi.isZoneNew()) {
      //Erase the Zone from the Main Map
      if (this.mSelectedZoneUi.mShape != null) {
        this.mMainMapStage.removeChild(this.mSelectedZoneUi.mShape);
      }
      this.eraseZonePerimeterPointsAndName(this.mSelectedZoneUi);

      //Remove the Zone from the mZoneUis array
      this.mZoneUis.splice(this.mZoneUis.indexOf(this.mSelectedZoneUi), 1);

      //Set the selected Zone to null
      this.selectZoneUi(null);
    } else {
      //This is an existing Zone... Call the Service to try to delete it
      this.mZoneService
        .deleteZoneInstances([this.mSelectedZoneUi.mZone.id])
        .then(
          success => {
            //Erase the Zone from the Main Map
            if (this.mSelectedZoneUi.mShape != null) {
              this.mMainMapStage.removeChild(this.mSelectedZoneUi.mShape);
            }
            this.eraseZonePerimeterPointsAndName(this.mSelectedZoneUi);

            //Remove the Zone from the mZoneUis array
            this.mZoneUis.splice(this.mZoneUis.indexOf(this.mSelectedZoneUi), 1);

            //Set the selected Zone to null
            this.selectZoneUi(null);
          },
          error => this.handleError(error)
        );
    }
  }

  async updateZoneClicked(): Promise<void> {
    console.log("Update Zone Clicked! Changed? " + this.mSelectedZoneUi.isZoneChanged());
    if (this.mSelectedZoneUi.isZoneChanged() && !this.mIsDrawingZone) {
      try {
        await this.saveZoneUi(this.mSelectedZoneUi);
      } catch (error) {
        this.handleError(error);
      }
    }
  }

  async saveZoneUi(ui: ZoneUi): Promise<void> {
    if (ui != null) {
      if (ui.isZoneNew()) {
        const savedZone = await this.mZoneService.createZoneInstance(ui.mZone);
        ui.mOriginalZone = savedZone;
        ui.mZone = JSON.parse(JSON.stringify(savedZone));
      } else if (ui.isZoneChanged()) {
        const savedZone = await this.mZoneService.updateZoneInstance(ui.mZone);
        ui.mOriginalZone = savedZone;
        ui.mZone = JSON.parse(JSON.stringify(savedZone));
      }
    }
  }

  async createZoneClicked(): Promise<void> {
    console.log("Create Zone Clicked!");
    if (this.mSelectedZoneUi != null && this.mSelectedZoneUi.isZoneNew() && !this.mIsDrawingZone) {
      try {
        await this.saveZoneUi(this.mSelectedZoneUi);
      } catch (error) {
        this.handleError(error);
      }
    }
  }





  async selectMainTab(tab: MainTabType): Promise<void> {
    if (this.mSelectedMainTab != tab) {
      switch (tab) {
        case MainTabType.LOCATION:
        case MainTabType.SUBLOCATIONS:
        case MainTabType.ZONES:
        case MainTabType.OBJECTS: {
          if (this.mSelectedLocation == null) {
            return;
          }
        }
      }

      //Set the mIsPlacing Flag to false to exit out of the "Placing" mode
      this.mIsPlacing = false;

      //Cancel any scaling that is going on
      this.setScaleCanceled();

      //If changing off the Sub Location's Tab, unselect any selected Sub Location
      if (this.mSelectedMainTab == MainTabType.SUBLOCATIONS) {
        this.selectSubLocationUi(null);
      }

      //If changing off the Zone's Tab, unselect any selected Zone
      if (this.mSelectedMainTab == MainTabType.ZONES) {
        this.selectZoneUi(null);
      }

      //If changing off the Object's Tab, unselect any selected Antenna/Reader/Device/Asset and re-select the ANTENNAS tab
      if (this.mSelectedMainTab == MainTabType.OBJECTS) {
        this.selectAntennaUi(null);
        this.selectReaderUi(null);
        this.selectAssetUi(null);
        this.selectDeviceUi(null);
        this.selectObjectTab(ObjectTabType.ANTENNAS);
      }

      console.log("Selected Main Tab Change! " + this.mSelectedMainTab + " => " + tab);
      this.mSelectedMainTab = tab;
    }
  }

  async selectObjectTab(tab: ObjectTabType): Promise<void> {
    if (this.mSelectedObjectTab != tab) {
      console.log("Selected Object Tab changing: " + this.mSelectedObjectTab + " => " + tab);

      //Set the mIsPlacing Flag to false to exit out of the "Placing" mode
      this.mIsPlacing = false;

      //When switching off a Tab, set any selected Item for the previous Tab to null
      switch (this.mSelectedObjectTab) {
        case ObjectTabType.ANTENNAS: {
          this.selectAntennaUi(null);
          break;
        }
        case ObjectTabType.READERS: {
          this.selectReaderUi(null);
          break;
        }
        case ObjectTabType.DEVICES: {
          this.selectDeviceUi(null);
          break;
        }
        case ObjectTabType.ASSETS: {
          this.selectAssetUi(null);
          break;
        }
      }

      this.mSelectedObjectTab = tab;
    }
  }





  refreshAntennas(): void {
    this.mAntennaService
      .getAllAntennaInstances()
      .then(
        antennas => {
          this.mAntennas = antennas;
          antennas.forEach(antenna => {
            const ui = new AntennaUi();
            ui.mAntenna = antenna;
            ui.mOriginalAntenna = JSON.parse(JSON.stringify(antenna));
            if (antenna.locationInstance != null && this.mSelectedLocation.id == antenna.locationInstance.id) {
              this.mAntennasInLocation.push(ui);
              this.drawAntenna(ui);
            } else {
              this.mAntennasNotInLocation.push(ui);
            }
          });

        },
        error => this.handleError(error)
      );
  }

  redrawAntenna(ui: AntennaUi): void {
    if (ui.mIcon != null) {
      const iconScale = this.getIconScale();
      ui.mIcon.scaleX = iconScale;
      ui.mIcon.scaleY = iconScale;
      ui.mIcon.x = this.translateMetersToPixels(ui.mAntenna.position.x) - (ui.mIcon.image.width * iconScale / 2);
      ui.mIcon.y = this.translateMetersToPixels(ui.mAntenna.position.y) - (ui.mIcon.image.height * iconScale / 2);

      //If the Highlight is present, redraw that as well
      if (ui.mHighlight != null) {
        this.redrawAntennaHighlight(ui);
      }
    }
  }

  eraseAntenna(ui: AntennaUi): void {
    if (ui.mIcon != null) {
      this.mMainMapStage.removeChild(ui.mIcon);
      ui.mIcon = null;
    }
  }

  drawAntenna(ui: AntennaUi): void {
    if (ui.mAntenna.position != null && ui.mAntenna.position.x != null && ui.mAntenna.position.y != null) {
      const image = document.createElement('img');
      image.crossOrigin = 'Anyonymous'; //Enable Cross Origin (Need to do it this way due to not being able to inside EaselJS itself - https://stackoverflow.com/questions/41468523/easeljs-cross-domain-images-workaround)
      image.src = this.getIconPathForAntenna(ui);

      ui.mIcon = new createjs.Bitmap(image);

      const component = this;
      ui.mIcon.image.onload = function(): void {
        const iconScale = component.getIconScale();
        ui.mIcon.scaleX = iconScale;
        ui.mIcon.scaleY = iconScale;
        ui.mIcon.x = component.translateMetersToPixels(ui.mAntenna.position.x) - (ui.mIcon.image.width * iconScale / 2);
        ui.mIcon.y = component.translateMetersToPixels(ui.mAntenna.position.y) - (ui.mIcon.image.height * iconScale / 2);
        component.mMainMapStage.addChild(ui.mIcon);

        if (ui == component.mSelectedAntennaUi) {
          component.drawAntennaHighlight(ui);
        }
      }

      ui.mIcon.on('click', function(event: any): void {
        console.log("Antenna Clicked! Antenna: " + ui.mAntenna.name);
        if (component.areThingsSelectable()) {
          if (!component.mCancelClickEvent) {
            component.selectAntennaUi(ui);
          }
          event.stopPropagation();
        }
      });

      ui.mIcon.on('pressmove', function(event: any): void {
        console.log("Antenna Dragged! Antenna: " + ui.mAntenna.name);
        component.selectAntennaUi(ui);

        //Get the new Point value for the EaselJS Event
        const newPoint = component.getPointFromEaselEvent(event);

        //Set the new X/Y to the Antenna
        ui.mAntenna.position.x = newPoint.x;
        ui.mAntenna.position.y = newPoint.y;

        //Redraw the Antenna so that the Icon/Highlight is in the new position
        component.redrawAntenna(ui);
      });
    }
  }

  async selectAntennaUi(ui: AntennaUi): Promise<void> {
    if (ui != this.mSelectedAntennaUi) {
      console.log("Selecting Antenna: " + (ui == null? "*NULL*" : ui.mAntenna.name));

      //Set the mIsPlacing Flag to false to exit out of the "Placing" mode
      this.mIsPlacing = false;

      //Unhighlight the Antenna that is being de-selected
      if (this.mSelectedAntennaUi != null) {
        this.eraseAntennaHighlight(this.mSelectedAntennaUi);
      }

      this.mSelectedAntennaUi = ui;

      if (this.mSelectedAntennaUi != null) {
        //Switch to the "OBJECTS" Tab
        this.showObjectsTab();

        //Switch to the "ANTENNAS" Sub-tab
        this.selectObjectTab(ObjectTabType.ANTENNAS);

        //Highlight the selected Antenna
        if (ui.mIcon != null) {
          this.drawAntennaHighlight(ui);
        }
      }
    }
  }

  drawAntennaHighlight(ui: AntennaUi): void {
    //Create the Image used to wrap the Icon to show the Antenna being highlighted
    const image = document.createElement('img');
    image.crossOrigin = 'Anyonymous'; //Enable Cross Origin (Need to do it this way due to not being able to inside EaselJS itself - https://stackoverflow.com/questions/41468523/easeljs-cross-domain-images-workaround)
    image.src = 'assets/images/highlight-circle.png';

    ui.mHighlight = new createjs.Bitmap(image);
    const component = this;
    ui.mHighlight.image.onload = function(): void {
      const iconScale = component.getIconScale();
      ui.mHighlight.scaleX = (ui.mIcon.image.width * iconScale * 2) / ui.mHighlight.image.width;
      ui.mHighlight.scaleY = (ui.mIcon.image.height * iconScale * 2) / ui.mHighlight.image.height;
      ui.mHighlight.x = component.translateMetersToPixels(ui.mAntenna.position.x) - (ui.mHighlight.image.width * ui.mHighlight.scaleX / 2);
      ui.mHighlight.y = component.translateMetersToPixels(ui.mAntenna.position.y) - (ui.mHighlight.image.height * ui.mHighlight.scaleY / 2);

      component.mMainMapStage.addChild(ui.mHighlight);
    };
  }

  eraseAntennaHighlight(ui: AntennaUi): void {
    if (ui != null && ui.mHighlight != null) {
      this.mMainMapStage.removeChild(ui.mHighlight);
      ui.mHighlight = null;
    }
  }

  redrawAntennaHighlight(ui: AntennaUi): void {
    if (ui.mHighlight != null) {
      const iconScale = this.getIconScale();
      ui.mHighlight.scaleX = (ui.mIcon.image.width * iconScale * 2) / ui.mHighlight.image.width;
      ui.mHighlight.scaleY = (ui.mIcon.image.height * iconScale * 2) / ui.mHighlight.image.height;
      ui.mHighlight.x = this.translateMetersToPixels(ui.mAntenna.position.x) - (ui.mHighlight.image.width * ui.mHighlight.scaleX / 2);
      ui.mHighlight.y = this.translateMetersToPixels(ui.mAntenna.position.y) - (ui.mHighlight.image.height * ui.mHighlight.scaleY / 2);
    }
  }





  refreshReaders(): void {
    this.mReaderService
      .getAllReaderInstances()
      .then(
        readers => {
          this.mReaders = readers;
          readers.forEach(reader => {
            const ui = new ReaderUi();
            ui.mReader = reader;
            ui.mOriginalReader = JSON.parse(JSON.stringify(reader));
            if (reader.location != null && this.mSelectedLocation.id == reader.location.id) {
              this.mReadersInLocation.push(ui);
              this.drawReader(ui);
            } else {
              this.mReadersNotInLocation.push(ui);
            }
          });
        },
        error => this.handleError(error)
      );
  }

  redrawReader(ui: ReaderUi): void {
    if (ui.mIcon != null) {
      const iconScale = this.getIconScale();
      ui.mIcon.scaleX = iconScale;
      ui.mIcon.scaleY = iconScale;
      ui.mIcon.x = this.translateMetersToPixels(ui.mReader.position.x) - (ui.mIcon.image.width * iconScale / 2);
      ui.mIcon.y = this.translateMetersToPixels(ui.mReader.position.y) - (ui.mIcon.image.height * iconScale / 2);

      //If the Highlight is present, redraw that as well
      if (ui.mHighlight != null) {
        this.redrawReaderHighlight(ui);
      }
    }
  }

  eraseReader(ui: ReaderUi): void {
    if (ui.mIcon != null) {
      this.mMainMapStage.removeChild(ui.mIcon);
      ui.mIcon = null;
    }
  }

  drawReader(ui: ReaderUi): void {
    if (ui.mReader.position != null && ui.mReader.position.x != null && ui.mReader.position.y != null) {
      const image = document.createElement('img');
      image.crossOrigin = 'Anyonymous'; //Enable Cross Origin (Need to do it this way due to not being able to inside EaselJS itself - https://stackoverflow.com/questions/41468523/easeljs-cross-domain-images-workaround)
      image.src = this.getIconPathForReader(ui);

      ui.mIcon = new createjs.Bitmap(image);

      const component = this;
      ui.mIcon.image.onload = function(): void {
        const iconScale = component.getIconScale();
        ui.mIcon.scaleX = iconScale;
        ui.mIcon.scaleY = iconScale;
        ui.mIcon.x = component.translateMetersToPixels(ui.mReader.position.x) - (ui.mIcon.image.width * iconScale / 2);
        ui.mIcon.y = component.translateMetersToPixels(ui.mReader.position.y) - (ui.mIcon.image.height * iconScale / 2);
        component.mMainMapStage.addChild(ui.mIcon);

        if (ui == component.mSelectedReaderUi) {
          component.drawReaderHighlight(ui);
        }
      }

      ui.mIcon.on('click', function(event: createjs.MouseEvent): void {
        console.log("Reader Clicked! Reader: " + ui.mReader.name);
        if (component.areThingsSelectable()) {
          if (!component.mCancelClickEvent) {
            component.selectReaderUi(ui);
          }
          event.stopPropagation();
        }
      });

      ui.mIcon.on('pressmove', function(event: any): void {
        console.log("Reader Dragged! Reader: " + ui.mReader.name);
        component.selectReaderUi(ui);

        //Get the new Point value for the EaselJS Event
        const newPoint = component.getPointFromEaselEvent(event);

        //Set the new X/Y to the Reader
        ui.mReader.position.x = newPoint.x;
        ui.mReader.position.y = newPoint.y;

        //Redraw the Reader so that the Icon/Highlight is in the new position
        component.redrawReader(ui);
      });
    }
  }

  async selectReaderUi(ui: ReaderUi): Promise<void> {
    if (ui != this.mSelectedReaderUi) {
      console.log("Selecting Reader: " + (ui == null? "*NULL*" : ui.mReader.name));

      //Set the mIsPlacing Flag to false to exit out of the "Placing" mode
      this.mIsPlacing = false;

      //Remove the Highlight from any Reader that is being de-selected
      if (this.mSelectedReaderUi != null) {
        this.eraseReaderHighlight(this.mSelectedReaderUi);
      }

      this.mSelectedReaderUi = ui;

      if (this.mSelectedReaderUi != null) {
        //Switch to the "OBJECTS" Tab
        this.showObjectsTab();

        //Switch to the "READERS" Sub-tab
        this.selectObjectTab(ObjectTabType.READERS);

        //Highlight the selected Reader
        if (ui.mIcon != null) {
          this.drawReaderHighlight(ui);
        }
      }
    }
  }

  drawReaderHighlight(ui: ReaderUi): void {
    //Create the Image used to wrap the Icon to show the Reader being highlighted
    const image = document.createElement('img');
    image.crossOrigin = 'Anyonymous'; //Enable Cross Origin (Need to do it this way due to not being able to inside EaselJS itself - https://stackoverflow.com/questions/41468523/easeljs-cross-domain-images-workaround)
    image.src = 'assets/images/highlight-circle.png';

    ui.mHighlight = new createjs.Bitmap(image);
    const component = this;
    ui.mHighlight.image.onload = function(): void {
      const iconScale = component.getIconScale();
      ui.mHighlight.scaleX = (ui.mIcon.image.width * iconScale * 2) / ui.mHighlight.image.width;
      ui.mHighlight.scaleY = (ui.mIcon.image.height * iconScale * 2) / ui.mHighlight.image.height;
      ui.mHighlight.x = component.translateMetersToPixels(ui.mReader.position.x) - (ui.mHighlight.image.width * ui.mHighlight.scaleX / 2);
      ui.mHighlight.y = component.translateMetersToPixels(ui.mReader.position.y) - (ui.mHighlight.image.height * ui.mHighlight.scaleY / 2);

      component.mMainMapStage.addChild(ui.mHighlight);
    };
  }

  eraseReaderHighlight(ui: ReaderUi): void {
    if (ui != null && ui.mHighlight != null) {
      this.mMainMapStage.removeChild(ui.mHighlight);
      ui.mHighlight = null;
    }
  }

  redrawReaderHighlight(ui: ReaderUi): void {
    if (ui.mHighlight != null) {
      const iconScale = this.getIconScale();
      ui.mHighlight.scaleX = (ui.mIcon.image.width * iconScale * 2) / ui.mHighlight.image.width;
      ui.mHighlight.scaleY = (ui.mIcon.image.height * iconScale * 2) / ui.mHighlight.image.height;
      ui.mHighlight.x = this.translateMetersToPixels(ui.mReader.position.x) - (ui.mHighlight.image.width * ui.mHighlight.scaleX / 2);
      ui.mHighlight.y = this.translateMetersToPixels(ui.mReader.position.y) - (ui.mHighlight.image.height * ui.mHighlight.scaleY / 2);
    }
  }





  refreshDevices(): void {
    this.mObjectService
      .getAllDeviceObjectInstances()
      .then(
        devices => {
          this.mDevices = devices;
          devices.forEach(device => {
            const ui = new ObjectUi();
            ui.mObject = device;
            ui.mOriginalObject = JSON.parse(JSON.stringify(device));
            if (device.locationInstance != null && this.mSelectedLocation.id == device.locationInstance.id) {
              this.mDevicesInLocation.push(ui);
              this.drawObject(ui);

              const component = this;
              ui.mIcon.on('click', function(event: any): void {
                console.log("Device Clicked! Device: " + ui.mObject.name);
                if (component.areThingsSelectable()) {
                  if (!component.mCancelClickEvent) {
                    component.selectDeviceUi(ui);
                  }
                  event.stopPropagation();
                }
              });
              ui.mIcon.on('pressmove', function(event: any): void {
                console.log("Device Dragged! Device: " + ui.mObject.name);
                component.selectDeviceUi(ui);

                //Get the new Point value for the EaselJS Event
                const newPoint = component.getPointFromEaselEvent(event);

                //Set the new X/Y to the Asset
                ui.mObject.position.x = newPoint.x;
                ui.mObject.position.y = newPoint.y;

                //Redraw the Asset so that the Icon/Highlight is in the new position
                component.redrawObject(ui);
              });
            } else {
              this.mDevicesNotInLocation.push(ui);
            }
          });
        },
        error => this.handleError(error)
      );
  }

  refreshAssets(): void {
    this.mObjectService
      .getAllAssetObjectInstances()
      .then(
        assets => {
          this.mAssets = assets;
          assets.forEach(asset => {
            const ui = new ObjectUi();
            ui.mObject = asset;
            ui.mOriginalObject = JSON.parse(JSON.stringify(asset));
            if (asset.locationInstance != null && this.mSelectedLocation.id == asset.locationInstance.id) {
              this.mAssetsInLocation.push(ui);
              this.drawObject(ui);

              const component = this;
              ui.mIcon.on('click', function(event: createjs.MouseEvent): void {
                console.log("Asset Clicked! Asset: " + ui.mObject.name);
                if (component.areThingsSelectable()) {
                  if (!component.mCancelClickEvent) {
                    component.selectAssetUi(ui);
                  }
                  event.stopPropagation();
                }
              });
              ui.mIcon.on('pressmove', function(event: any): void {
                console.log("Asset Dragged! Asset: " + ui.mObject.name);
                component.selectAssetUi(ui);

                //Get the new Point value for the EaselJS Event
                const newPoint = component.getPointFromEaselEvent(event);

                //Set the new X/Y to the Asset
                ui.mObject.position.x = newPoint.x;
                ui.mObject.position.y = newPoint.y;

                //Redraw the Asset so that the Icon/Highlight is in the new position
                component.redrawObject(ui);
              });
            } else {
              this.mAssetsNotInLocation.push(ui);
            }
          });
        },
        error => this.handleError(error)
      );
  }

  redrawObject(ui: ObjectUi): void {
    if (ui.mIcon != null) {
      const iconScale = this.getIconScale();
      ui.mIcon.scaleX = iconScale;
      ui.mIcon.scaleY = iconScale;
      ui.mIcon.x = this.translateMetersToPixels(ui.mObject.position.x) - (ui.mIcon.image.width * iconScale / 2);
      ui.mIcon.y = this.translateMetersToPixels(ui.mObject.position.y) - (ui.mIcon.image.height * iconScale / 2);

      //If the Highlight exists, redraw it as well
      if (ui.mHighlight != null) {
        this.redrawObjectHighlight(ui);
      }
    }
  }

  eraseObject(ui: ObjectUi): void {
    if (ui.mIcon != null) {
      this.mMainMapStage.removeChild(ui.mIcon);
      ui.mIcon = null;
    }
  }

  drawObject(ui: ObjectUi): void {
    if (ui.mObject.position != null && ui.mObject.position.x != null && ui.mObject.position.y != null) {
      const image = document.createElement('img');
      image.crossOrigin = 'Anyonymous'; //Enable Cross Origin (Need to do it this way due to not being able to inside EaselJS itself - https://stackoverflow.com/questions/41468523/easeljs-cross-domain-images-workaround)
      image.src = this.getIconPathForObject(ui);

      ui.mIcon = new createjs.Bitmap(image);

      const iconScale = this.getIconScale();
      ui.mIcon.scaleX = iconScale;
      ui.mIcon.scaleY = iconScale;

      const component = this;
      ui.mIcon.image.onload = function(): void {
        ui.mIcon.x = component.translateMetersToPixels(ui.mObject.position.x) - (ui.mIcon.image.width * iconScale / 2);
        ui.mIcon.y = component.translateMetersToPixels(ui.mObject.position.y) - (ui.mIcon.image.height * iconScale / 2);
        component.mMainMapStage.addChild(ui.mIcon);

        if (ui == component.mSelectedAssetUi || ui == component.mSelectedDeviceUi) {
          component.redrawObjectHighlight(ui);
        }
      }
    }
  }

  async selectAssetUi(ui: ObjectUi): Promise<void> {
    if (this.mSelectedAssetUi != ui) {
      console.log("Selecting Asset: " + (ui == null? "*NULL*" : ui.mObject.name));

      //Set the mIsPlacing Flag to false to exit out of the "Placing" mode
      this.mIsPlacing = false;

      //Remove the Highlight from any Asset that is being de-selected
      if (this.mSelectedAssetUi != null) {
        this.eraseObjectHighlight(this.mSelectedAssetUi);
      }

      this.mSelectedAssetUi = ui;

      if (this.mSelectedAssetUi != null) {
        //Switch to the "OBJECTS" Tab
        this.showObjectsTab();

        //Switch to the "ASSETS" Sub-tab
        this.selectObjectTab(ObjectTabType.ASSETS);

        //Highlight the selected Asset
        if (ui.mIcon != null) {
          this.drawObjectHighlight(ui);
        }
      }
    }
  }

  async selectDeviceUi(ui: ObjectUi): Promise<void> {
    if (this.mSelectedDeviceUi != ui) {
      console.log("Selecting Device: " + (ui == null? "*NULL*" : ui.mObject.name));

      //Set the mIsPlacing Flag to false to exit out of the "Placing" mode
      this.mIsPlacing = false;

      //Remove the Highlight from any Device that is being de-selected
      if (this.mSelectedDeviceUi != null) {
        this.eraseObjectHighlight(this.mSelectedDeviceUi);
      }

      this.mSelectedDeviceUi = ui;

      if (this.mSelectedDeviceUi != null) {
        //Switch to the "OBJECTS" Tab
        this.showObjectsTab();

        //Switch to the "DEVICES" Sub-tab
        this.selectObjectTab(ObjectTabType.DEVICES);

        //Highlight the selected Device
        if (ui.mIcon != null) {
          this.drawObjectHighlight(ui);
        }
      }
    }
  }

  drawObjectHighlight(ui: ObjectUi): void {
    console.log("Drawing Highlight for Object: " + ui.mObject.name);

    //Create the Image used to wrap the Icon to show the Object being highlighted
    const image = document.createElement('img');
    image.crossOrigin = 'Anyonymous'; //Enable Cross Origin (Need to do it this way due to not being able to inside EaselJS itself - https://stackoverflow.com/questions/41468523/easeljs-cross-domain-images-workaround)
    image.src = 'assets/images/highlight-circle.png';

    ui.mHighlight = new createjs.Bitmap(image);
    const component = this;
    ui.mHighlight.image.onload = function(): void {
      const iconScale = component.getIconScale();
      ui.mHighlight.scaleX = (ui.mIcon.image.width * iconScale * 2) / ui.mHighlight.image.width;
      ui.mHighlight.scaleY = (ui.mIcon.image.height * iconScale * 2) / ui.mHighlight.image.height;
      ui.mHighlight.x = component.translateMetersToPixels(ui.mObject.position.x) - (ui.mHighlight.image.width * ui.mHighlight.scaleX / 2);
      ui.mHighlight.y = component.translateMetersToPixels(ui.mObject.position.y) - (ui.mHighlight.image.height * ui.mHighlight.scaleY / 2);

      component.mMainMapStage.addChild(ui.mHighlight);
    };
  }

  eraseObjectHighlight(ui: ObjectUi): void {
    if (ui != null && ui.mHighlight != null) {
      console.log("Removing Highlight for Object: " + ui.mObject.name);
      this.mMainMapStage.removeChild(ui.mHighlight);
      ui.mHighlight = null;
    }
  }

  redrawObjectHighlight(ui: ObjectUi): void {
    if (ui.mHighlight != null) {
      const iconScale = this.getIconScale();
      ui.mHighlight.scaleX = (ui.mIcon.image.width * iconScale * 2) / ui.mHighlight.image.width;
      ui.mHighlight.scaleY = (ui.mIcon.image.height * iconScale * 2) / ui.mHighlight.image.height;
      ui.mHighlight.x = this.translateMetersToPixels(ui.mObject.position.x) - (ui.mHighlight.image.width * ui.mHighlight.scaleX / 2);
      ui.mHighlight.y = this.translateMetersToPixels(ui.mObject.position.y) - (ui.mHighlight.image.height * ui.mHighlight.scaleY / 2);
    }
  }





  removeAntennaClicked(): void {
    console.log("Remove Antenna Clicked!");
    if (this.mSelectedAntennaUi != null) {
      //Erase the Antenna Icon/Highlight
      this.eraseAntennaHighlight(this.mSelectedAntennaUi);
      this.eraseAntenna(this.mSelectedAntennaUi);

      //Update the Antenna's Location/Position to null (removed from everywhere)
      this.mSelectedAntennaUi.mAntenna.locationInstance = null;
      this.mSelectedAntennaUi.mAntenna.position = null;

      //Update the Antennas In Location and Antennas Not In Location arrays
      const index = this.mAntennasInLocation.indexOf(this.mSelectedAntennaUi);
      if (index > -1) {
        this.mAntennasInLocation.splice(index, 1);
        this.mAntennasNotInLocation.push(this.mSelectedAntennaUi);
      }
    }
  }

  removeReaderClicked(): void {
    console.log("Remove Reader Clicked!");
    if (this.mSelectedReaderUi != null) {
      //Erase the Antenna Icon/Highlight
      this.eraseReaderHighlight(this.mSelectedReaderUi);
      this.eraseReader(this.mSelectedReaderUi);

      //Update the Reader's Location/Position to null (removed from everywhere)
      this.mSelectedReaderUi.mReader.location = null;
      this.mSelectedReaderUi.mReader.position = null;

      //Update the Readers In Location and Readers Not In Location arrays
      const index = this.mReadersInLocation.indexOf(this.mSelectedReaderUi);
      if (index > -1) {
        this.mReadersInLocation.splice(index, 1);
        this.mReadersNotInLocation.push(this.mSelectedReaderUi);
      }
    }
  }

  removeAssetClicked(): void {
    console.log("Remove Asset Clicked!");
    if (this.mSelectedAssetUi != null) {
      //Erase the Asset Icon/Highlight
      this.eraseObjectHighlight(this.mSelectedAssetUi);
      this.eraseObject(this.mSelectedAssetUi);

      //Update the Asset's Location/Position to null (removed from everywhere)
      this.mSelectedAssetUi.mObject.locationInstance = null;
      this.mSelectedAssetUi.mObject.position = null;

      //Update the Assets In Location and Assets Not In Location arrays
      const index = this.mAssetsInLocation.indexOf(this.mSelectedAssetUi);
      if (index > -1) {
        this.mAssetsInLocation.splice(index, 1);
        this.mAssetsNotInLocation.push(this.mSelectedAssetUi);
      }
    }
  }

  removeDeviceClicked(): void {
    console.log("Remove Device Clicked!");
    if (this.mSelectedDeviceUi != null) {
      //Erase the Object Icon/Highlight
      this.eraseObjectHighlight(this.mSelectedDeviceUi);
      this.eraseObject(this.mSelectedDeviceUi);

      //Update the Device's Location/Position to null (removed from everywhere)
      this.mSelectedDeviceUi.mObject.locationInstance = null;
      this.mSelectedDeviceUi.mObject.position = null;

      //Update the Devices In Location and Devices Not In Location arrays
      const index = this.mDevicesInLocation.indexOf(this.mSelectedDeviceUi);
      if (index > -1) {
        this.mDevicesInLocation.splice(index, 1);
        this.mDevicesNotInLocation.push(this.mSelectedDeviceUi);
      }
    }
  }





  placeAntennaClicked(): void {
    console.log("placeAntennaClicked()");
    this.mIsPlacing = true;
  }

  placeReaderClicked(): void {
    console.log("placeReaderClicked()");
    this.mIsPlacing = true;
  }

  placeAssetClicked(): void {
    console.log("placeAssetClicked()");
    this.mIsPlacing = true;
  }

  placeDeviceClicked(): void {
    console.log("placeDeviceClicked()");
    this.mIsPlacing = true;
  }





  async saveAntennaClicked(): Promise<void> {
    console.log("saveAntennaClicked()");
    try {
      await this.saveAntennaUiPosition(this.mSelectedAntennaUi);
    } catch (error) {
      this.handleError(error);
    }
  }

  async saveAntennaUiPosition(ui: AntennaUi): Promise<void> {
    if (ui != null && ui.isAntennaChanged()) {
      const antennaInstanceId = ui.mAntenna.id;
      const locationId = ui.mAntenna.locationInstance == null? null : ui.mAntenna.locationInstance.id;
      const position = ui.mAntenna.position;

      const savedAntenna = await this.mAntennaService.setAntennaInstancePosition(antennaInstanceId, locationId, position);

      ui.mAntenna = savedAntenna;
      ui.mOriginalAntenna = JSON.parse(JSON.stringify(savedAntenna));
    }
  }

  async saveReaderClicked(): Promise<void> {
    console.log("saveReaderClicked()");
    try {
      await this.saveReaderUiPosition(this.mSelectedReaderUi);
    } catch (error) {
      this.handleError(error);
    }
  }

  async saveReaderUiPosition(ui: ReaderUi): Promise<void> {
    if (ui != null && ui.isReaderChanged()) {
      const readerInstanceId = ui.mReader.id;
      const locationId = ui.mReader.location == null? null : ui.mReader.location.id;
      const position = ui.mReader.position;

      const savedReader = await this.mReaderService.setReaderInstancePosition(readerInstanceId, locationId, position)

      ui.mReader = savedReader;
      ui.mOriginalReader = JSON.parse(JSON.stringify(savedReader));
    }
  }

  async saveDeviceClicked(): Promise<void> {
    console.log("saveDeviceClicked()");
    try {
      await this.saveObjectUiPosition(this.mSelectedDeviceUi);
    } catch (error) {
      this.handleError(error);
    }
  }

  async saveAssetClicked(): Promise<void> {
    console.log("saveAssetClicked()");
    try {
      await this.saveObjectUiPosition(this.mSelectedAssetUi);
    } catch (error) {
      this.handleError(error);
    }
  }

  async saveObjectUiPosition(ui: ObjectUi): Promise<void> {
    if (ui != null && ui.isObjectChanged()) {
      const objectInstanceId = ui.mObject.id;
      const locationId = ui.mObject.locationInstance == null? null : ui.mObject.locationInstance.id;
      const position = ui.mObject.position;

      const updatedObject = await this.mObjectService.setObjectInstancePosition(objectInstanceId, locationId, position);

      ui.mObject = updatedObject
      ui.mOriginalObject = JSON.parse(JSON.stringify(updatedObject));
    }
  }





  showLocationTab(): void {
    $('.nav-tabs a[href="#' + this.mLocationTabId + '"]').tab('show');
    this.selectMainTab(MainTabType.LOCATION);
  }

  showSubLocationsTab(): void {
    $('.nav-tabs a[href="#' + this.mSubLocationsTabId + '"]').tab('show');
    this.selectMainTab(MainTabType.SUBLOCATIONS);
  }

  showZonesTab(): void {
    $('.nav-tabs a[href="#' + this.mZonesTabId + '"]').tab('show');
    this.selectMainTab(MainTabType.ZONES);
  }

  showObjectsTab(): void {
    $('.nav-tabs a[href="#' + this.mObjectsTabId + '"]').tab('show');
    this.selectMainTab(MainTabType.OBJECTS);
  }


  setZCoordinateClicked(): void {
    console.log("Set Z Coordinate Clicked!");
    if (!this.mIsDrawingSubLocation && !this.mIsDrawingZone && !this.mIsPlacing) {
      this.mSetZCoordinateDialog.show();
    }
  }


  getPointFromEaselEvent(event: createjs.MouseEvent): Point {
    return this.getPointFromStageXY(event.stageX, event.stageY);
  }

  getPointFromStageXY(stageX: number, stageY: number): Point {
    //Get the current Scale applied to the Main Map (Inverse it so that it can reverse the scaling of the values)
    const iconScale = this.getIconScale();

    //Scale the Stage X/Y values
    const scaledStageX = (stageX) * iconScale;
    const scaledStageY = (stageY) * iconScale;

    //Scale the Pan X/Y values
    const scaledPanX = (this.mMainMapStage.x) * iconScale;
    const scaledPanY = (this.mMainMapStage.y) * iconScale;

    //Get the Point's new X/Y values in Pixels
    const newXPixels = scaledStageX - scaledPanX;
    const newYPixels = scaledStageY - scaledPanY;

    //Convert the Point's new X/Y values to Meters
    const retVal = new Point();
    retVal.x = this.translatePixelsToMeters(newXPixels);
    retVal.y = this.translatePixelsToMeters(newYPixels);

    return retVal;
  }





  getIconScale(): number {
    return (1 / this.mMainMapStage.scaleX);
  }

  getIconPathForAntenna(ui: AntennaUi): string {
    return this.mImageService.getIconSourceForAntennaInstance(ui.mAntenna);
  }

  getIconPathForReader(ui: ReaderUi): string  {
    return this.mImageService.getIconSourceForReaderInstance(ui.mReader);
  }

  getIconPathForObject(ui: ObjectUi): string {
    return this.mImageService.getIconSourceForObjectInstance(ui.mObject);
  }





  zoomInClicked(): void {
    this.setMainMapScaleIndex(this.mMainMapScaleIndex + 1);
  }

  zoomOutClicked(): void {
    this.setMainMapScaleIndex(this.mMainMapScaleIndex - 1);
  }





  /*
   * Site Footer Event Handlers - Called when their respective options are clicked on the Sites Footer
   */
  setScaleClicked(): void {
    console.log("Set Scale Clicked!");
    if (!this.mIsDrawingSubLocation && !this.mIsDrawingZone && !this.mIsPlacing) {
      this.mSetScaleStartDialog.show();
    }
  }

  uploadMapClicked(): void {
    console.log("Upload Map Clicked!");
    this.mLocationImageFileInput.nativeElement.click();
  }

  addZoneClicked(): void {
    console.log("Add Zone Clicked!");
    if (this.mSelectedZoneUi == null || !(this.mSelectedZoneUi.isZoneNew() && this.mIsDrawingZone)) {
      const newZone = new ZoneUi();
      newZone.mZone = new ZoneInstance();
      if (this.mZoneTypes.length > 0) {
        newZone.mZone.zone = this.mZoneTypes[0];
      }
      newZone.mZone.locationInstance = this.mSelectedLocation;
      newZone.mZone.color = new Color();
      newZone.mZone.color.red = 255;
      newZone.mZone.color.green = 255;
      newZone.mZone.color.blue = 0;
      newZone.mZone.textColor = new Color();
      newZone.mZone.textColor.red = 255;
      newZone.mZone.textColor.green = 255;
      newZone.mZone.textColor.blue = 0;

      this.mZoneUis.push(newZone);
      this.selectZoneUi(newZone);
      this.mIsDrawingZone = true;
      this.mTemporaryPerimeterPoints = [];
    }
  }

  addSubLocationClicked(): void {
    console.log("Add SubLocation Clicked!");
    if (this.mSelectedSubLocationUi == null || !(this.mSelectedSubLocationUi.isSubLocationNew() && this.mIsDrawingSubLocation)) {
      const newSubLocation = new SubLocationUi();
      newSubLocation.mSubLocation = new LocationInstance();
      if (this.mLocationTypes.length > 0) {
        newSubLocation.mSubLocation.location = this.mLocationTypes[0];
      }
      newSubLocation.mSubLocation.parent = new ParentLocationWrapper();
      newSubLocation.mSubLocation.parent.instance = this.mSelectedLocation;
      newSubLocation.mSubLocation.color = new Color();
      newSubLocation.mSubLocation.color.red = 0;
      newSubLocation.mSubLocation.color.green = 255;
      newSubLocation.mSubLocation.color.blue = 0;
      newSubLocation.mSubLocation.textColor = new Color();
      newSubLocation.mSubLocation.textColor.red = 0;
      newSubLocation.mSubLocation.textColor.green = 255;
      newSubLocation.mSubLocation.textColor.blue = 0;

      this.mSubLocationUis.push(newSubLocation);
      this.selectSubLocationUi(newSubLocation);
      this.mIsDrawingSubLocation = true;
      this.mTemporaryPerimeterPoints = [];
    }
  }

  toggleTrackingClicked(): void {
    console.log("Toggle Tracking Clicked!");
  }





  cancelClicked(): void {
    console.log("Cancel Clicked!");
    this.mIsPlacing = false;
    this.cancelDrawingZone();
    this.cancelDrawingSubLocation();
    this.setScaleCanceled();
  }





  cancelDrawingSubLocation(): void {
    if (this.mIsDrawingSubLocation) {
      this.mIsDrawingSubLocation = false;

      //If this is a new Sub Location that has yet to be drawn for the first time, just cancel the creation and remove it from the list...
      const subLocationsIndex = this.mSubLocationUis.indexOf(this.mSelectedSubLocationUi);
      if (  this.mSelectedSubLocationUi != null
        &&  (this.mSelectedSubLocationUi.isSubLocationNew() && (this.mSelectedSubLocationUi.mSubLocation.perimeterPoints == null || this.mSelectedSubLocationUi.mSubLocation.perimeterPoints.length == 0))
      ) {
        //Check to see if it is still in the mSubLocationUis array, it might have been removed if DELETE was clicked.
        if (subLocationsIndex > -1) {
          this.mSubLocationUis.splice(subLocationsIndex, 1);
        }
      } else {
        //Make sure that the Zone is still in the mSubLocationUis array, as it might have been removed if DELETE was clicked.  If deleted, don't draw it.
        if (subLocationsIndex > -1) {
          //This is either an existing Sub Location or a new one that has already been drawn at least once... Just reset it back to what it was
          this.drawSubLocation(this.mSelectedSubLocationUi);
          this.drawSubLocationPerimeterPointsAndName(this.mSelectedSubLocationUi);
        }
      }
      this.selectSubLocationUi(null);

      //Erase any Temporary Points / Area drawn
      this.eraseTemporaryPerimeterPoints();
      this.eraseTemporaryPerimeterShape();
    }
  }

  finishDrawingSubLocation(): void {
    console.log("finishDrawingSubLocation()");
    if (this.mTemporaryPerimeterPoints != null && this.mTemporaryPerimeterPoints.length > 2) {
      //Erase the old Sub Location Points + Area
      this.eraseSubLocationPerimeterPointsAndName(this.mSelectedSubLocationUi);
      this.eraseSubLocation(this.mSelectedSubLocationUi);

      //Finish Drawing the Sub Location... Set the Sub Location's Points to the new values from the Temporary Points
      this.mSelectedSubLocationUi.mSubLocation.perimeterPoints = [];
      this.mTemporaryPerimeterPoints.forEach(ui => {
        this.mSelectedSubLocationUi.mSubLocation.perimeterPoints.push(ui.mPoint);
      });

      //Erase the Temporary Perimeter Points + Area
      this.eraseTemporaryPerimeterPoints();
      this.eraseTemporaryPerimeterShape();

      //Draw the Sub Location, along with it's Perimeter Points and Name since it is selected...
      this.drawSubLocation(this.mSelectedSubLocationUi);
      this.drawSubLocationPerimeterPointsAndName(this.mSelectedSubLocationUi);

      //Update the mIsDrawingSubLocation Flag, flipping it to false to indicate that the User is no longer drawing a Sub Location
      this.mIsDrawingSubLocation = false;
    }
  }





  cancelDrawingZone(): void {
    if (this.mIsDrawingZone) {
      this.mIsDrawingZone = false;

      //If this is a new Zone that has yet to be drawn for the first time, just cancel the creation and remove it from the list...
      const zonesIndex = this.mZoneUis.indexOf(this.mSelectedZoneUi);
      if (  this.mSelectedZoneUi != null
        &&  (this.mSelectedZoneUi.isZoneNew() && (this.mSelectedZoneUi.mZone.perimeterPoints == null || this.mSelectedZoneUi.mZone.perimeterPoints.length == 0))
      ) {
        //Check to see if it is still in the mZoneUis array, it might have been removed if DELETE was clicked.
        if (zonesIndex > -1) {
          this.mZoneUis.splice(zonesIndex, 1);
        }
      } else {
        //Make sure that the Zone is still in the mZoneUis array, as it might have been removed if DELETE was clicked.  If deleted, don't draw it.
        if (zonesIndex > -1) {
          //This is either an existing Zone or a new one that has already been drawn at least once... Just reset it back to what it was
          this.drawZone(this.mSelectedZoneUi);
          this.drawZonePerimeterPointsAndName(this.mSelectedZoneUi);
        }
      }
      this.selectZoneUi(null);

      //Erase any Temporary Points / Area drawn
      this.eraseTemporaryPerimeterPoints();
      this.eraseTemporaryPerimeterShape();
    }
  }

  finishDrawingZone(): void {
    console.log("finishDrawingZone()");
    if (this.mTemporaryPerimeterPoints != null && this.mTemporaryPerimeterPoints.length > 2) {
      //Erase the old Zone's Points/Name + Area
      this.eraseZonePerimeterPointsAndName(this.mSelectedZoneUi);
      this.eraseZone(this.mSelectedZoneUi);

      //Finish Drawing the Zone... Set the Zone's Points to the new values from the Temporary Points
      this.mSelectedZoneUi.mZone.perimeterPoints = [];
      this.mTemporaryPerimeterPoints.forEach(ui => {
        this.mSelectedZoneUi.mZone.perimeterPoints.push(ui.mPoint);
      });

      //Erase the Temporary Perimeter Points + Area
      this.eraseTemporaryPerimeterPoints();
      this.eraseTemporaryPerimeterShape();

      //Draw the Zone, along with it's Perimeter Points and Name since it is selected...
      this.drawZone(this.mSelectedZoneUi);
      this.drawZonePerimeterPointsAndName(this.mSelectedZoneUi);

      //Update the mIsDrawingZone Flag, flipping it to false to indicate that the User is no longer drawing a Zone
      this.mIsDrawingZone = false;
    }
  }





  redrawTemporaryZonePoints(): void {
    if (this.mTemporaryPerimeterPoints != null) {
      this.mTemporaryPerimeterPoints.forEach(ui => {
        this.erasePerimeterPoint(ui);
        this.drawPerimeterPoint(ui);
      });
    }
  }

  eraseTemporaryPerimeterPoints(): void {
    if (this.mTemporaryPerimeterPoints != null) {
      this.mTemporaryPerimeterPoints.forEach(ui => this.erasePerimeterPoint(ui));
      this.mTemporaryPerimeterPoints = null;
    }
  }

  eraseTemporaryPerimeterShape(): void {
    if (this.mTemporaryShape != null) {
      this.mMainMapStage.removeChild(this.mTemporaryShape);
      this.mTemporaryShape = null;
    }
  }





  drawSubLocationClicked(): void {
    console.log("Draw Sub Location Clicked!");
    if (this.mSelectedSubLocationUi != null) {
      //Erase any Temporary Points / Area drawn
      this.eraseTemporaryPerimeterPoints();
      this.eraseTemporaryPerimeterShape();

      //Erase the old Sub Location
      this.eraseSubLocationPerimeterPointsAndName(this.mSelectedSubLocationUi);
      this.eraseSubLocation(this.mSelectedSubLocationUi)

      //Set up the variables used for the "Draw Sub Location" functionality
      this.mTemporaryPerimeterPoints = [];
      this.mIsDrawingSubLocation = true;
    }
  }

  drawZoneClicked(): void {
    console.log("Draw Zone Clicked!");
    if (this.mSelectedZoneUi != null) {
      //Erase any Temporary Points / Area drawn
      this.eraseTemporaryPerimeterPoints();
      this.eraseTemporaryPerimeterShape();

      //Erase the old Zone
      this.eraseZonePerimeterPointsAndName(this.mSelectedZoneUi);
      this.eraseZone(this.mSelectedZoneUi);

      //Set up the variables used for the "Draw Zone" functionality
      this.mTemporaryPerimeterPoints = [];
      this.mIsDrawingZone = true;
    }
  }





  setScaleStartOkClicked(): void {
    console.log("scaleStartOkClicked()");
    this.mScaleStartPoint = null;
    this.mScaleStopPoint = null;
    this.mIsSettingScale = true;
  }

  setScaleCanceled(): void {
    console.log("setScaleCanceled()");
    this.mIsSettingScale = false;
    if (this.mScaleStartPoint != null) {
      this.erasePerimeterPoint(this.mScaleStartPoint);
    }
    this.mScaleStartPoint = null;

    if (this.mScaleStopPoint != null) {
      this.erasePerimeterPoint(this.mScaleStopPoint);
    }
    this.mScaleStopPoint = null;
  }

  setScaleFinished(newScale: number): void {
    console.log("setScaleFinished(" + newScale + ")");

    //Set the new Scale to the Location
    this.mEditableLocation.scale = newScale;

    //Refresh the dimensions of the minimap
    this.refreshMiniMapMeasurements();

    //Redraw any Zones
    this.mZoneUis.forEach(ui => this.drawZone(ui));

    //Redraw any Objects that are displayed
    this.mAntennasInLocation.forEach(ui => this.redrawAntenna(ui));
    this.mReadersInLocation.forEach(ui => this.redrawReader(ui));
    this.mDevicesInLocation.forEach(ui => this.redrawObject(ui));
    this.mAssetsInLocation.forEach(ui => this.redrawObject(ui));
  }

  setZCoordinate(zcoordinate: number): void {
    console.log("setZCoordinate(" + zcoordinate);

    if (this.mSelectedAntennaUi) {
      console.log("setZCoordinate for Antenna");
      this.mSelectedAntennaUi.mAntenna.position.z = zcoordinate;
    } else if (this.mSelectedAssetUi) {
      console.log("setZCoordinate for Asset");
      this.mSelectedAssetUi.mObject.position.z = zcoordinate;
    } else if (this.mSelectedDeviceUi) {
      console.log("setZCoordinate for Device");
      this.mSelectedDeviceUi.mObject.position.z = zcoordinate;
    } else if (this.mSelectedReaderUi) {
      console.log("setZCoordinate for Reader");
      this.mSelectedReaderUi.mReader.position.z = zcoordinate;
    }
  }



  createLocationClicked(): void {
    console.log("createLocationClicked()");

    //Cancel anything that is currently going on...
    this.cancelClicked();

    //Display the CreateSiteDialogComponent
    this.mCreateSiteDialog.show();
  }

  async createdLocation(newLocation: LocationInstance): Promise<void> {
    await this.refreshSiteLocations();
    const newSite = this.mSiteLocations.find(location => location.id == newLocation.id);
    this.selectLocation(newSite);
  }







  onImageFileChanged(event: any): void {
    if (event.target.files.length === 1) {
      this.submitImage(event.target.files[0]);
    }
  }

  resetImage(): void {
    if (this.mEditableLocation != null) {
      this.mEditableLocation.image = null;
      this.drawLocation();
    }
  }

  async submitImage(file: File): Promise<void> {
    this.mUploadingDialog.show();

    const formData = new FormData();
    formData.append("file", file);

    let image:Image = null;
    try {
      image = await this.mImageService.uploadImageFile(file);
    } catch (error) {
      this.mLocationImageFileInput.nativeElement.value = "";
      this.mUploadingDialog.hide();
      this.handleError(error);
      return;
    }

    console.log(`Uploaded Image: ${JSON.stringify(image)}`);
    if (image != null && this.mEditableLocation != null) {
      this.mEditableLocation.image = image;
      this.drawLocation();
    }
    
    this.mLocationImageFileInput.nativeElement.value = "";
    this.mUploadingDialog.hide();
  }







  handleError(error: any): void {
    console.error("SitesComponent.handleError() - " + error);
    if (error != null) {
      const response: Response = error;
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, response.text().toString());
    }
  }





  rgbToHex(r: number, g: number, b: number): string {
    return "#" + this.colorToHex(r) + this.colorToHex(g) + this.colorToHex(b);
  }

  colorToHex(c: number): string {
    const h = c.toString(16);
    return h.length == 1? "0" + h : h;
  }

  convertHexToColor(hex: string): Color {
    const rv = new Color();
    rv.red = Number.parseInt(hex.substring(1, 3), 16)
    rv.green = Number.parseInt(hex.substring(3, 5), 16);
    rv.blue = Number.parseInt(hex.substring(5, 7), 16);
    return rv;
  }

  convertColorToHex(color: Color): string {
    return "#" + this.colorToHex(color.red) + this.colorToHex(color.green) + this.colorToHex(color.blue);
  }




  areThingsSelectable(): boolean {
    return !this.mIsDrawingZone && !this.mIsSettingScale && !this.mIsDrawingSubLocation;
  }

}










enum MainTabType {
  SUBLOCATIONS = 'SUBLOCATIONS',
  SITES = 'SITES',
  LOCATION = 'LOCATION',
  ZONES = 'ZONES',
  OBJECTS = 'OBJECTS'
}

enum ObjectTabType {
  ANTENNAS = 'ANTENNAS',
  READERS = 'READERS',
  ASSETS = 'ASSETS',
  DEVICES = 'DEVICES'
}

class PerimeterPointUi {
  mPoint: Point;
  mShape: createjs.Shape;

  hitTest(layerX: number, layerY: number): boolean {
    if (this.mShape != null) {
      const globalToLocalPoint = this.mShape.globalToLocal(layerX, layerY);
      return this.mShape.hitTest(globalToLocalPoint.x, globalToLocalPoint.y);
    } else {
      return false;
    }
  }
}

class SubLocationUi {
  mSubLocation: LocationInstance;
  mOriginalSubLocation: LocationInstance;

  mShape: createjs.Shape;
  mNameField: createjs.Text;

  mPerimeterPointUis: PerimeterPointUi[];

  isSubLocationNew(): boolean {
    return this.mOriginalSubLocation == null;
  }

  isSubLocationChanged(): boolean {
    return !(
          this.mOriginalSubLocation != null
      &&  this.mSubLocation.name == this.mOriginalSubLocation.name
      &&  this.mSubLocation.location.id == this.mOriginalSubLocation.location.id
      &&  this.mSubLocation.color.red == this.mOriginalSubLocation.color.red
      &&  this.mSubLocation.color.green == this.mOriginalSubLocation.color.green
      &&  this.mSubLocation.color.blue == this.mOriginalSubLocation.color.blue
      &&  this.mSubLocation.textColor.red == this.mOriginalSubLocation.textColor.red
      &&  this.mSubLocation.textColor.green == this.mOriginalSubLocation.textColor.green
      &&  this.mSubLocation.textColor.blue == this.mOriginalSubLocation.textColor.blue
      &&  this.mSubLocation.description == this.mOriginalSubLocation.description
      &&  !this.didPointsChange()
    );
  }

  didPointsChange(): boolean {
    if (this.mSubLocation.perimeterPoints == null && this.mOriginalSubLocation.perimeterPoints == null) {
      return false;
    } else if (this.mSubLocation.perimeterPoints == null || this.mOriginalSubLocation.perimeterPoints == null) {
      return true;
    } else if (this.mSubLocation.perimeterPoints.length != this.mOriginalSubLocation.perimeterPoints.length) {
      return true;
    } else {
      for (let i = 0; i < this.mSubLocation.perimeterPoints.length; i ++) {
        const pointOne = this.mSubLocation.perimeterPoints[i];
        const pointTwo = this.mOriginalSubLocation.perimeterPoints[i];
        if (!(pointOne.x == pointTwo.x && pointOne.y == pointTwo.y)) {
          return true;
        }
      }
    }
    return false;
  }

}

class ZoneUi {
  mZone: ZoneInstance;
  mOriginalZone: ZoneInstance;

  mShape: createjs.Shape;
  mNameField: createjs.Text;

  mPerimeterPointUis: PerimeterPointUi[];

  isZoneNew(): boolean {
    return this.mOriginalZone == null;
  }

  isZoneChanged(): boolean {
    return !(
          this.mOriginalZone != null
      &&  this.mZone.name == this.mOriginalZone.name
      &&  this.mZone.zone.id == this.mOriginalZone.zone.id
      &&  this.mZone.color.red == this.mOriginalZone.color.red
      &&  this.mZone.color.green == this.mOriginalZone.color.green
      &&  this.mZone.color.blue == this.mOriginalZone.color.blue
      &&  this.mZone.textColor.red == this.mOriginalZone.textColor.red
      &&  this.mZone.textColor.green == this.mOriginalZone.textColor.green
      &&  this.mZone.textColor.blue == this.mOriginalZone.textColor.blue
      &&  this.mZone.description == this.mOriginalZone.description
      &&  !this.didPointsChange()
      );
  }

  didPointsChange(): boolean {
    if (this.mZone.perimeterPoints == null && this.mOriginalZone.perimeterPoints == null) {
      return false;
    } else if (this.mZone.perimeterPoints == null || this.mOriginalZone.perimeterPoints == null) {
      return true;
    } else if (this.mZone.perimeterPoints.length != this.mOriginalZone.perimeterPoints.length) {
      return true;
    } else {
      for (let i = 0; i < this.mZone.perimeterPoints.length; i ++) {
        const pointOne = this.mZone.perimeterPoints[i];
        const pointTwo = this.mOriginalZone.perimeterPoints[i];
        if (!(pointOne.x == pointTwo.x && pointOne.y == pointTwo.y)) {
          return true;
        }
      }
    }
    return false;
  }

}

class AntennaUi {
  mAntenna: AntennaInstance;
  mOriginalAntenna: AntennaInstance;

  mIcon: createjs.Bitmap;
  mHighlight: createjs.Bitmap;

  isAntennaChanged(): boolean {
    return !(
      !this.didLocationChange() && !this.didPositionChange()
    );
  }

  didLocationChange(): boolean {
    if (this.mAntenna.locationInstance == null && this.mOriginalAntenna.locationInstance == null) {
      return false;
    } else if (this.mAntenna.locationInstance == null || this.mOriginalAntenna.locationInstance == null) {
      return true;
    } else {
      return this.mAntenna.locationInstance.id != this.mOriginalAntenna.locationInstance.id;
    }
  }

  didPositionChange(): boolean {
    if (this.mAntenna.position == null && this.mOriginalAntenna.position == null) {
      return false;
    } else if (this.mAntenna.position == null || this.mOriginalAntenna.position == null) {
      return true;
    } else {
      return this.mAntenna.position.x != this.mOriginalAntenna.position.x || this.mAntenna.position.y != this.mOriginalAntenna.position.y
        || this.mAntenna.position.z != this.mOriginalAntenna.position.z;
    }
  }
}

class ReaderUi {
  mReader: ReaderInstance;
  mOriginalReader: ReaderInstance;

  mIcon: createjs.Bitmap;
  mHighlight: createjs.Bitmap;

  isReaderChanged(): boolean {
    return !(
      !this.didLocationChange() && !this.didPositionChange()
    );
  }

  didLocationChange(): boolean {
    if (this.mReader.location == null && this.mOriginalReader.location == null) {
      return false;
    } else if (this.mReader.location == null || this.mOriginalReader.location == null) {
      return true;
    } else {
      return this.mReader.location.id != this.mOriginalReader.location.id;
    }
  }

  didPositionChange(): boolean {
    if (this.mReader.position == null && this.mOriginalReader.position == null) {
      return false;
    } else if (this.mReader.position == null || this.mOriginalReader.position == null) {
      return true;
    } else {
      return this.mReader.position.x != this.mOriginalReader.position.x || this.mReader.position.y != this.mOriginalReader.position.y
        || this.mReader.position.z != this.mOriginalReader.position.z;
    }
  }
}

class ObjectUi {
  mObject: ObjectInstance;
  mOriginalObject: ObjectInstance;

  mIcon: createjs.Bitmap;
  mHighlight: createjs.Bitmap;

  isObjectChanged(): boolean {
    return !(
      !this.didLocationChange() && !this.didPositionChange()
    );
  }

  didLocationChange(): boolean {
    if (this.mObject.locationInstance == null && this.mOriginalObject.locationInstance == null) {
      return false;
    } else if (this.mObject.locationInstance == null || this.mOriginalObject.locationInstance == null) {
      return true;
    } else {
      return this.mObject.locationInstance.id != this.mOriginalObject.locationInstance.id;
    }
  }

  didPositionChange(): boolean {
    if (this.mObject.position == null && this.mOriginalObject.position == null) {
      return false;
    } else if (this.mObject.position == null || this.mOriginalObject.position == null) {
      return true;
    } else {
      return this.mObject.position.x != this.mOriginalObject.position.x || this.mObject.position.y != this.mOriginalObject.position.y
        || this.mObject.position.z != this.mOriginalObject.position.z;
    }
  }
}
